import { SET_ANSWER_TRIGGER,
  buildFlowGroupId, DEFAULT_FLOW_CONFIG, getUnreachableBranchIds, isIncompleteTrigger, isRetryTrigger,
  isSensorTrigger,
  NO_SENSOR_DATA_TRIGGER,
  isNoSensorDataTrigger,
  isPoorSensorDataTrigger,
  isValueRangeBlockType, starvedBlockCauses, targetedFiresIn, valueDefaultIncorrectUnreachable, valueFeedbackSubsumedByCorrect,
  computeRotationAcceptedRanges, computeCompassAcceptedRanges, computeSliderAcceptedRanges, computeTiltAcceptedRanges,
  type ArrivalKeep, type ArrivalPolicy, type ArrivalRule,
  EXIT_BLOCK_TYPES, type ExitBlock, type ExitContent,
  type AttemptCompare, type BranchCondition, type FeedbackState, type FlowAction, type FlowChange, type FlowCondition, type FlowEffect,
  type GradingMode,
  type FlowRule, type SensorRule, type SensorRung, type TaskFlowConfig,
  uncertaintyAsked,
  type NumericalConditionType,
} from '@agavi/blocks';

import type { Block, Task } from '@/types/experience';

import { BLOCK_META } from '@/components/task/block-library';
import { stripHtmlForLabel } from '@/components/task/formula-plaintext';
import { catchAllTitle, numericalHasFormatGate, ORDERING_SURVEY_CATCH_ALL } from '@/lib/block-condition-chips';
import { stateChipLabel } from '@/lib/block-state-labels';

/** Every function in this file only ever reads a branch's condition fields (`id`/`operator`/
 * `threshold`/`thresholdHigh`) and its own `feedback.states` — never a payload-specific field like
 * MCQ/Dropdown/Keyword/FITB's `roles` or Ordering's `order`. This is the honest type for that: it's
 * structurally satisfied by EVERY block's own branch shape (`ConditionalRoleBranch`,
 * `ConditionalOrderBranch`), so Flow's branch-awareness genuinely works identically for all of
 * them, which the old `ConditionalRoleBranch[]`-typed `branchesOf` (asserting a `.roles` field this
 * file never reads) obscured rather than documented. */
interface FlowBranchShape extends BranchCondition {
  feedback: { states: FeedbackState[] };
}

export interface FeedbackStateShape {
  id: string;
  type: 'default' | 'targeted';
  trigger: string;
  title: string;
  evaluation: 'neutral' | 'correct' | 'incorrect';
  /** Present only for a state collapsed from a branching block's `conditionalBranches` — always
   * `undefined` for every non-branching block type, and for any MCQ/Dropdown/Keyword/FITB with no
   * branches authored (see feedbackStatesOf's own doc comment for why). `branchIds` is the stable,
   * order-independent set used for drift detection (getConditionSignature); `ordinals`/`total` are
   * live 1-based display positions, recomputed on every call like everything else in this file —
   * display only, never compared for drift (branches can only be appended/removed, never
   * reordered, so an ordinal can silently renumber when an unrelated branch is deleted). */
  branchScope?: { branchIds: string[]; ordinals: number[]; total: number };
  /**
   * The (version, branch) cells a BANKED block's state was authored in — `undefined` for every
   * block with no bank, which is the `branchScope` case above and is left byte-identical.
   *
   * BRANCH IS NESTED INSIDE VERSION, not a second independent axis: `addVersion` gives every
   * duplicated branch a fresh id, so no branch id is ever shared between two versions. A label
   * naming a set of versions beside a set of branch ids would therefore describe cells that
   * cannot exist. Branches are compared across versions by ORDINAL instead — what a teacher sees,
   * and what versions duplicated from each other actually share.
   *
   * `cells` is stable and order-independent (drift only); `parts` is live display positions,
   * recomputed every call like everything else here.
   */
  scope?: ConditionScope;
}

/** One cell of the (version, branch) product. An axis is absent where the block has none. */
export interface ScopeCell { versionId?: string; branchId?: string }

/** One contributing version's display position, and which of ITS branches contributed. */
export interface ScopeVersionPart { ordinal: number; branchOrdinals: number[]; branchTotal: number }

export interface ConditionScope {
  cells: ScopeCell[];
  parts: ScopeVersionPart[];
  /** How many versions the block has. The branch-only case never reaches here. */
  versionTotal: number;
}

/** The three trigger names a block's positive slot can carry, depending on its current grading
 * mode — see use-input-block-feedback.ts's positiveTriggerForMode. An inclusion list, not an
 * exclusion list: block-specific extra default states (Numerical's math-error/sig-fig traps,
 * GPS/Compass's accuracy gates) are ALSO `type: 'default'`, so excluding "everything that isn't
 * unanswered/incomplete/incorrect" would wrongly sweep them up as "the positive state" too. */
const POSITIVE_TRIGGERS = new Set(['correct', 'any_answer', 'any_response']);

function isPositiveTrigger(trigger: string): boolean {
  return POSITIVE_TRIGGERS.has(trigger);
}

function rawFeedbackStatesOf(block: Block | undefined): FeedbackState[] {
  const feedback = (block?.data as { feedback?: { states?: FeedbackState[] } } | undefined)?.feedback;
  return feedback?.states ?? [];
}

/**
 * A banked block's versions, or `undefined` for every block without a bank.
 *
 * Read for Flow because the top-level `feedback`/`conditionalBranches` of a banked block are the
 * VESTIGIAL BASE COPY: `writeOptionsVersion` preserves them from the stored block on every write,
 * so they are frozen at the moment the bank was created and never updated again. Flow read them
 * anyway, which on a real block with 0 base states and 3 in v1 meant Flow offered no conditions
 * at all for it.
 *
 * `length > 0`, not `> 1`: a bank always holds at least two (`removeVersion` collapses the last
 * two back to a plain block), but if one ever did survive alone its states still live in it
 * rather than in the frozen copy.
 */
interface FlowVersionShape {
  id: string;
  feedback?: { states?: FeedbackState[] };
  conditionalBranches?: FlowBranchShape[];
}

function versionsOf(block: Block | undefined): FlowVersionShape[] | undefined {
  const versions = (block?.data as { versions?: FlowVersionShape[] } | undefined)?.versions;
  return versions && versions.length > 0 ? versions : undefined;
}

/** A block's own `conditionalBranches` (variable-driven correctness), if it has any authored —
 * `undefined` (not an empty array) whenever there are none, so every caller can treat "has
 * branches" as one truthiness check rather than an explicit length comparison. */
function branchesOf(block: Block | undefined): FlowBranchShape[] | undefined {
  const branches = (block?.data as { conditionalBranches?: FlowBranchShape[] } | undefined)?.conditionalBranches;
  return branches && branches.length > 0 ? branches : undefined;
}

/**
 * The single source of truth every other function in this file is built on — every one of them
 * is a filter, lookup, or diagnostic over exactly this function's output, never over
 * `block.data.feedback.states` directly. That's deliberate: it means fixing branch-awareness here
 * once fixes the Special-event picker, the Any Incomplete/Any Incorrect (or Neutral)/All Correct
 * buckets, and every contradiction/staleness check in one place, rather than four.
 *
 * For a block with no `conditionalBranches` authored (every non-branching block type, and any
 * MCQ/Dropdown/Keyword/FITB that simply hasn't turned branching on): byte-identical to the old
 * behavior, reading only the block's own top-level `feedback.states`. Zero behavior change.
 *
 * For a block WITH branches: collapses every branch's own `feedback.states` into one entry per
 * distinct `(type, trigger, evaluation)` triple, tagging each with which branches contributed it
 * (see FeedbackStateShape's `branchScope`). The block's top-level `feedback.states` is
 * deliberately NOT included once branches exist — it only ever fires as a fail-closed fallback
 * when no branch's equation resolves (a coverage gap), which is already surfaced separately as its
 * own authoring-time warning on the block; folding it in here would produce a group whose
 * branch-scope is empty and reads as broken ("(Branches )") rather than meaningful.
 *
 * Collapsing by `(type, trigger, evaluation)` (not just trigger) is what lets this treat default
 * and targeted states identically: a targeted state's title is 100% mechanically derived from its
 * stable option/blank/keyword identity, so two branches' copies of the same trigger always
 * produce byte-identical titles — but its EVALUATION genuinely can differ per branch (it mirrors
 * that branch's own role assignment for the option), and even a default state's evaluation isn't
 * always locked (No Answer/Incomplete Answer have a real, UI-exposed neutral/incorrect cycle a
 * teacher can set independently per branch). Keying on evaluation too means a state that's
 * "Incorrect" in branches 2/4 but "Neutral" in branch 5 correctly becomes two separate conditions,
 * not one that silently glosses over a real difference.
 *
 * A targeted state whose evaluation is `'correct'` in a given branch is functionally unreachable
 * there — correct-first-terminal means it's never even consulted once that branch's own answer is
 * correct — so that branch's instance is excluded from grouping entirely, same as how the positive
 * slot is already excluded from namedStatesFor/incorrectOrNeutralStatesOf. This must NOT delete it
 * from being found at all, though: positiveStateOf legitimately wants to discover a
 * correct-evaluation state for the "All Correct" bucket — default states never need this
 * exclusion, since only the positive slot can be evaluation-`'correct'` among them.
 *
 * A targeted state with blank body text is NOT excluded — deliberately. Leaving a targeted
 * feedback's message empty (flag an option as incorrect with no explanation) is a legitimate
 * authoring choice, surfaced only as a soft warning on the block's own collapsed answer bar, never
 * a blocking error — Flow honors that same judgment rather than second-guessing it. This does mean
 * a branch-seeded shell (forking a branch clones every existing targeted state into it, content
 * reset to blank via `resetContentIfCustomized` until the teacher writes something branch-specific
 * — see `cloneFeedbackWithFreshIds`/`seedBranchFeedback` in each block's own component) shows up in
 * Flow too, even before anyone has reviewed it for that particular branch — a known, accepted
 * tradeoff, not an oversight: there's no data-model signal today that distinguishes "auto-cloned,
 * never touched" from "a teacher deliberately added this and chose to leave it blank," and the
 * product owner chose simplicity over adding one.
 *
 * A branch that `getUnreachableBranchIds` reports as fully shadowed by an earlier branch (dead —
 * `resolveMatchedBranch`'s first-match-wins can never actually select it, for any equation value)
 * is skipped ENTIRELY, contributing nothing to any group — the same "possible but not activated"
 * principle as Numerical's `inactiveReasonFor`/`mathErrorReachable`, just computed per-branch
 * instead of per-category. `total` (used to decide "(any branch)" vs. a named subset) counts only
 * REACHABLE branches, so a dead branch never forces an otherwise-universal condition to read as a
 * partial one — but branch ORDINALS (the numbers actually shown, e.g. "Branch 3") still reflect
 * true position in the full authored array, so they match what a teacher sees navigating branches
 * in the block's own editor, not a renumbering that would only make sense inside this function.
 */
/**
 * THE DEFAULT STATES A BLOCK HAS, which is not the same as the ones it has SAVED.
 *
 * Every block seeds No Answer, its mode's positive state and Default Incorrect through
 * `normalizeFeedback` at render time. They are not written to storage until something edits the
 * block, so a block that has merely been ADDED carries none of them — measured across the dev
 * database, that is about sixty blocks of every type, including ten of sixty-four graded MCQs.
 *
 * Every other reader in the app goes through a block's `initData`, so it sees the seeded set.
 * This file did not, and read `block.data` raw. Three faults all traced to that one bypass:
 *
 *   - All Correct's hint drew an EMPTY token list, so the "No student actions available" note
 *     disappeared the instant a task gained a block and nothing replaced it
 *   - a SURVEY block still holding a stale graded `correct` was listed as "Correct" rather than
 *     "Any Answer", because `positiveStateOf`'s fallback caught it
 *   - a block in Optional with nothing saved contributed no card at all
 *
 * `normalizeFeedback` is the canonical version of this and its own note records the identical bug
 * being fixed once before — thirteen stored blocks rendering with the whole Correct category
 * missing — but it cannot be reused here. It takes each block's `extraDefaults` (Numerical's math
 * errors, a sensor's no-measurement states), those lists live in the block components, and a
 * default state whose trigger is not among them is DROPPED. A lib that cannot enumerate them
 * would delete exactly the sensor states the rungs below are built from.
 *
 * So this is strictly ADDITIVE: it seeds the three standard slots when they are absent and
 * canonicalises a positive slot left under another mode's name. It never removes anything, which
 * is what makes it safe without the per-block lists. Titles are not seeded because nothing here
 * reads them — `defaultEntry` derives a card's label from the TRIGGER.
 */
function withSeededDefaults(block: Block | undefined, states: FeedbackStateShape[]): FeedbackStateShape[] {
  // `isGradableBlockType`, NOT `blockHasFeedback` — the latter asks whether a block has SAVED
  // feedback, which is false for precisely the blocks that need seeding. Its own doc says so.
  if (!block || !isGradableBlockType(block)) return states;
  const mode = (block.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded';
  const positive = mode === 'optional' ? 'any_response' : mode === 'survey' ? 'any_answer' : 'correct';

  /*
   * NOT canonicalised, only seeded.
   *
   * `normalizeFeedback` rewrites a positive slot left under another mode's name; the same move was
   * written here and then removed, because on every surface this file feeds it changed nothing.
   * A stale `correct` in a participation block is already inert — `inactiveReasonFor` filters it
   * out of `namedStatesFor`, and the seeded exact match below is what `positiveStateOf` finds, so
   * the card reads "Any Answer" either way. Measured both ways before taking it out.
   *
   * Titles are derived from the TRIGGER by `defaultEntry`, so nothing is lost by seeding a state
   * with no title rather than carrying one over.
   */
  const out = states;

  const has = (trigger: string) => out.some((st) => st.type === 'default' && st.trigger === trigger);
  const seeded: FeedbackStateShape[] = [];
  // Ids derived from the trigger rather than minted, for the reason `normalizeFeedback` gives:
  // these run during render, and a fresh id per render is one a Flow rule cannot match on.
  if (!has('unanswered')) seeded.push({ id: 'default-unanswered', type: 'default', trigger: 'unanswered', title: '', evaluation: 'neutral' });
  if (!has(positive)) seeded.push({ id: `default-${positive}`, type: 'default', trigger: positive, title: '', evaluation: 'correct' });
  if (!has('incorrect')) seeded.push({ id: 'default-incorrect', type: 'default', trigger: 'incorrect', title: '', evaluation: 'incorrect' });

  return seeded.length ? [...out, ...seeded] : out;
}

function feedbackStatesOf(block: Block | undefined): FeedbackStateShape[] {
  const versions = versionsOf(block);
  const states = versions ? versionedFeedbackStatesOf(block, versions) : unversionedFeedbackStatesOf(block);
  return withSeededDefaults(block, states);
}

/**
 * The same collapse, across the (version, branch) product instead of branches alone.
 *
 * Grouping is by BEHAVIOUR — one entry per distinct `(type, trigger, evaluation)` — so the
 * product never reaches the picker: six versions that treat an option identically give one
 * condition reading "(Any Version)". What the product decides is the SCOPE each entry carries.
 *
 * Both guarantees from the branch-only path hold per cell, and both are what stop Flow making a
 * false claim. A targeted state whose evaluation is 'correct' never contributes, and a state
 * joins a scope only in the cells where it was actually reachable — so on the canoe, where v1's
 * correct answer is v2's wrong answer, "Chose Positive" is skipped in v2 and scoped to version 1,
 * while the default Correct state is identical in both and collapses to "(Any Version, …)".
 * Opposite and correct answers for the two kinds of state, from one grouping key.
 */
function versionedFeedbackStatesOf(block: Block | undefined, versions: FlowVersionShape[]): FeedbackStateShape[] {
  const graded = ((block?.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded') === 'graded';
  const order: string[] = [];
  const groups = new Map<string, { title: string; type: FeedbackStateShape['type']; trigger: string; evaluation: FeedbackStateShape['evaluation']; cells: ScopeCell[] }>();
  /** Reachable branch count per version — the denominator "Any Branch" is measured against. */
  const branchTotals = new Map<string, number>();
  /** 1-based position among ALL that version's branches, matching the branch-only path: an
   *  unreachable branch still occupies its ordinal, so the numbers match the carousel. */
  const branchOrdinals = new Map<string, number>();
  const versionOrdinals = new Map(versions.map((v, i) => [v.id, i + 1]));

  const add = (s: FeedbackState, cell: ScopeCell) => {
    if (s.type === 'targeted' && s.evaluation === 'correct') return;
    const key = `${s.type} ${s.trigger} ${s.evaluation}`;
    let group = groups.get(key);
    if (!group) {
      group = { title: s.title, type: s.type, trigger: s.trigger, evaluation: s.evaluation, cells: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.cells.push(cell);
  };

  for (const version of versions) {
    // Branches are resolved ONLY in graded mode, same as the branch-only path — see its comment.
    const branches = graded && version.conditionalBranches?.length ? version.conditionalBranches : undefined;
    if (!branches) {
      branchTotals.set(version.id, 0);
      for (const s of (version.feedback?.states ?? []) as FeedbackState[]) add(s, { versionId: version.id });
      continue;
    }
    branches.forEach((b, i) => branchOrdinals.set(b.id, i + 1));
    const unreachable = getUnreachableBranchIds(branches);
    const reachable = branches.filter((b) => !unreachable.has(b.id));
    branchTotals.set(version.id, reachable.length);
    for (const branch of reachable) {
      for (const s of (branch.feedback?.states ?? []) as FeedbackState[]) {
        add(s, { versionId: version.id, branchId: branch.id });
      }
    }
  }

  const partsOf = (cells: ScopeCell[]): ScopeVersionPart[] => {
    const byVersion = new Map<string, Set<number>>();
    for (const cell of cells) {
      if (!cell.versionId) continue;
      let ords = byVersion.get(cell.versionId);
      if (!ords) { ords = new Set(); byVersion.set(cell.versionId, ords); }
      if (cell.branchId) ords.add(branchOrdinals.get(cell.branchId)!);
    }
    return [...byVersion.entries()]
      .map(([versionId, ords]) => ({
        ordinal: versionOrdinals.get(versionId)!,
        branchOrdinals: [...ords].sort((a, b) => a - b),
        branchTotal: branchTotals.get(versionId) ?? 0,
      }))
      .sort((a, b) => a.ordinal - b.ordinal);
  };

  return order.map((key) => {
    const group = groups.get(key)!;
    return {
      id: buildFlowGroupId({ type: group.type, trigger: group.trigger, evaluation: group.evaluation }),
      type: group.type,
      trigger: group.trigger,
      title: group.title,
      evaluation: group.evaluation,
      scope: { cells: group.cells, parts: partsOf(group.cells), versionTotal: versions.length },
    };
  });
}

function unversionedFeedbackStatesOf(block: Block | undefined): FeedbackStateShape[] {
  // Branches are resolved ONLY in graded mode — every evaluate() reads
  // `gradingMode === 'graded' ? resolveEffectiveOptions(...) : blockData.options`, so in
  // Participation or Optional the branch feedback sets are never consulted at all. Treating the
  // block as unbranched there stops Flow offering "(Any Branch) ..." conditions that cannot fire,
  // and stops the branch-scope prefix appearing on a mode where branches aren't even accessible.
  const graded = ((block?.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded') === 'graded';
  const branches = graded ? branchesOf(block) : null;
  if (!branches) {
    // Same exclusion the branch path below applies per-branch (see this function's own doc
    // comment) — a targeted state whose evaluation is 'correct' targets a correct-role
    // option/value directly (not its absent counterpart) and is functionally unreachable,
    // correct-first-terminal means it's never even consulted. Without branches authored this was
    // never filtered at all, so it leaked into Flow's own special-event picker as a selectable
    // condition that can structurally never fire.
    return rawFeedbackStatesOf(block).filter((s) => !(s.type === 'targeted' && s.evaluation === 'correct'));
  }

  const unreachableBranchIds = getUnreachableBranchIds(branches);
  const reachableBranches = branches.filter((b) => !unreachableBranchIds.has(b.id));
  const total = reachableBranches.length;
  const indexByBranchId = new Map(branches.map((b, i) => [b.id, i]));
  const order: string[] = [];
  const groups = new Map<string, { title: string; type: FeedbackStateShape['type']; trigger: string; evaluation: FeedbackStateShape['evaluation']; branchIds: string[] }>();

  for (const branch of reachableBranches) {
    for (const s of (branch.feedback?.states ?? []) as FeedbackState[]) {
      if (s.type === 'targeted' && s.evaluation === 'correct') continue;
      const key = `${s.type} ${s.trigger} ${s.evaluation}`;
      let group = groups.get(key);
      if (!group) {
        group = { title: s.title, type: s.type, trigger: s.trigger, evaluation: s.evaluation, branchIds: [] };
        groups.set(key, group);
        order.push(key);
      }
      if (!group.branchIds.includes(branch.id)) group.branchIds.push(branch.id);
    }
  }

  return order.map((key) => {
    const group = groups.get(key)!;
    const ordinals = group.branchIds.map((id) => indexByBranchId.get(id)! + 1).sort((a, b) => a - b);
    return {
      id: buildFlowGroupId({ type: group.type, trigger: group.trigger, evaluation: group.evaluation }),
      type: group.type,
      trigger: group.trigger,
      title: group.title,
      evaluation: group.evaluation,
      branchScope: { branchIds: group.branchIds, ordinals, total },
    };
  });
}

/** An accepted range plus each end's own open/closed boundary — `lt` is exclusive at its threshold
 * where `lte` is inclusive, and the reachability helpers below need that distinction to avoid
 * reporting a boundary-value feedback as unreachable. */
type AcceptedRangeShape = { lo: number; hi: number; loInclusive: boolean; hiInclusive: boolean };

/** Resolves a stored condition id to its live collapsed state. Tries a direct match against
 * `feedbackStatesOf`'s own (possibly-synthetic) ids first; if that fails, falls back to searching
 * the block's raw per-branch/top-level states by literal id and re-resolving to whichever
 * collapsed group that state's (type, trigger, evaluation) now belongs to. This second path is
 * what keeps an existing Flow rule from silently going stale the moment a teacher turns branching
 * on for its block — before this fix, that could never break a rule (branches were invisible to
 * Flow entirely); after it, a rule's stored id may suddenly need to resolve through a branch
 * instead of the top level, and this is where that happens. */
function resolveConditionId(block: Block | undefined, id: string): FeedbackStateShape | undefined {
  const collapsed = feedbackStatesOf(block);
  const direct = collapsed.find((s) => s.id === id);
  if (direct) return direct;
  /*
   * Where the RAW states live depends on whether the block is banked. On a banked block the
   * top-level copy is vestigial (see `versionsOf`), so searching it would fail to resolve a
   * stored condition that points at a state inside a version — and an unresolved condition is
   * reported as deleted, which is a rule silently going stale rather than an error.
   */
  const versions = versionsOf(block);
  const raw: FeedbackStateShape[] = versions
    ? versions.flatMap((v) => [
      ...((v.feedback?.states ?? []) as FeedbackStateShape[]),
      ...(v.conditionalBranches ?? []).flatMap((b) => (b.feedback?.states ?? []) as FeedbackStateShape[]),
    ])
    : (() => {
      const branches = branchesOf(block);
      if (!branches) return [];
      return [...rawFeedbackStatesOf(block), ...branches.flatMap((b) => (b.feedback?.states ?? []) as FeedbackStateShape[])];
    })();
  if (raw.length === 0) return undefined;
  const match = raw.find((s) => s.id === id);
  if (!match) return undefined;
  return collapsed.find((s) => s.type === match.type && s.trigger === match.trigger && s.evaluation === match.evaluation);
}

/** Looks up one specific state by id on a block — used wherever a caller already has a stale/
 * inactive state id in hand (from getStaleStateIds/getInactiveSelectedStateIds) and needs the
 * full state object back (for its title, evaluation, or inactive reason). */
export function getFeedbackState(block: Block, stateId: string): FeedbackStateShape | undefined {
  return resolveConditionId(block, stateId);
}

/** Joins branch ordinals into a display list ("2 and 3", "2, 3, and 4") — same connector-word
 * convention as numerical-block.tsx's joinWithConnector, kept as its own small copy here rather
 * than shared, since this is the only place in this file that needs it. Reuses the existing
 * `task.flow.card.connectorAnd` key (already used for exactly this purpose in
 * getRuleContradictions' stateLabels join) rather than adding a new connector string. */
function joinOrdinals(ordinals: number[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  const items = ordinals.map(String);
  const connector = t('task.flow.card.connectorAnd');
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} ${connector} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, ${connector} ${items[items.length - 1]}`;
}

/** The label to show for a state's chip in Flow — overrides the block's own title for the two
 * triggers with a fixed Flow-facing name that's shorter/clearer than the raw title (Numerical's
 * "Significant Figures Error" wraps awkwardly as a chip; the generic Default Incorrect state's
 * own title varies by grading mode). Every other state just shows its own title.
 *
 * When the state is branch-collapsed (see FeedbackStateShape's `branchScope`), PREFIXES a scope
 * qualifier so a teacher never mistakes "this condition" for meaning the same thing on every
 * submission: "(any branch)" when it was found in every currently-authored branch (deliberately
 * the same wording a plain, non-branching block's condition implicitly has — "always applies"),
 * or "(Branches 2, 3, and 4)" for a genuine subset. Deliberately a PREFIX, not a suffix — every
 * chip that renders this label truncates/clamps from the end (`flow-rule-row.tsx`), so a qualifier
 * appended after a long base title (a custom-order sequence, say) would be exactly what gets cut
 * off — the one piece of text that MUST survive truncation, since without it two differently-
 * scoped chips read as identical. Called "branch" here even though the block's own authoring UI
 * calls the same thing a "condition" — Flow already uses "condition" to mean "one selected entry
 * in a rule," so reusing that word here for a different concept would be ambiguous specifically in
 * this context. */
/**
 * What THIS block calls its catch-all — asked of the block, not of a map.
 *
 * Flow was a THIRD derivation of one name. The block's panel says "Any Other Answer" once a
 * target exists, its test bar enumerates the leftovers, and Flow said "Any Answer" always,
 * because `DEFAULT_TITLE_KEYS` is keyed on the trigger alone and a trigger cannot know whether
 * the block has been split. A teacher routing on a card the panel called "Any Other Answer"
 * picked one labelled "Any Answer" and had to trust they were the same card.
 *
 * `catchAllTitle` is that one derivation, so this hands it the two inputs it needs rather than
 * growing a fourth rule: whether anything is targeted, and what this block type calls "other".
 *
 * NOT enumerated here, deliberately. A Flow rung speaks about the TASK, often several blocks at
 * once, so "Any Other Answer" means "that block's leftovers" and the block's own panel is one
 * click away. Enumerating would duplicate a list that moves whenever the answer setup changes,
 * in a card with no room to say which block each interval belongs to.
 */
function catchAllTitleForBlock(
  block: Block | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | undefined {
  if (!block) return undefined;
  const data = block.data as {
    gradingMode?: GradingMode;
    feedback?: { states?: FeedbackStateShape[] };
  } | undefined;
  const mode = data?.gradingMode ?? 'graded';
  /*
   * "Targeted" means targeted AND able to fire HERE: a card authored in one mode is hidden, not
   * deleted, in the modes it cannot fire in, and a hidden card must not narrow the catch-all's
   * name any more than it narrows what the catch-all catches.
   */
  const hasTargeted =
    (data?.feedback?.states ?? []).some(x => x.type === 'targeted' && targetedFiresIn(x.trigger, mode))
    || (block.type === 'numerical'
      && numericalHasFormatGate(block.data as Parameters<typeof numericalHasFormatGate>[0]));
  /* A block whose answer is not an "answer" names its own leftover: Ordering's is an ORDER, and
     Sort's is an ARRANGEMENT. Without this Flow said "Any Other Answer" for a Participation Sort
     while the block beside it said "Any Other Arrangement" — one state, two surfaces, two names,
     which is the exact drift this module exists to prevent. */
  return catchAllTitle(mode, hasTargeted, t,
    block.type === 'ordering' ? ORDERING_SURVEY_CATCH_ALL
      : block.type === 'sort' ? 'blocks.sort.chipAnyOther'
        : undefined);
}

export function flowStateLabel(
  state: FeedbackStateShape,
  t: (key: string, opts?: Record<string, unknown>) => string,
  block?: Block,
  locale?: string,
): string {
  /*
   * TARGETED states derive; DEFAULT states are named.
   *
   * Flow names a CARD — it is asking which feedback you mean — and a default card's name is the
   * category it belongs to. A targeted card has no such name: its title IS its condition, derived
   * from its trigger, so deriving here gives the same string the block shows.
   *
   * This used to derive both, and for a value-range block that produced a Default Incorrect chip
   * reading "Anything but 320° to 40°" while the teacher's own block called the card "Incorrect".
   * Stating the leftover exactly (which is what the block's card does) was not the fix either: a
   * range does not tell you which card you are picking, which is the only question this label
   * answers. The ranges belong on the test bar, where the question is "what did the student do".
   */
  const canonical = block && state.type === 'targeted' ? stateChipLabel(block, state, t, locale ?? 'en') : null;
  const base = canonical
    ?? (state.trigger === 'incorrect' ? t('task.flow.card.defaultIncorrectOption')
    : state.trigger === 'notation_error' ? t('task.flow.card.notationErrorOption')
    : state.trigger === 'sigfig_error' ? t('task.flow.card.sigFigErrorOption')
    // The catch-all is the one default whose name depends on the BLOCK rather than the trigger —
    // see `catchAllTitleForBlock`. Falls through to the map when no block is in hand.
    : (state.trigger === 'any_answer' || state.trigger === 'any_response')
        && catchAllTitleForBlock(block, t) ? catchAllTitleForBlock(block, t)!
    // Every other default: the category's own name, so one block's renamed card cannot make the
    // same state read differently here than it does one rung up.
    : defaultTitleKey(state.trigger, block?.type) ? t(defaultTitleKey(state.trigger, block?.type)!)
    : state.title);
  if (state.scope) {
    const prefix = scopeLabel(state.scope, t);
    return prefix ? `${prefix} ${base}` : base;
  }
  const scope = state.branchScope;
  if (!scope || scope.total <= 1) return base;
  return scope.ordinals.length === scope.total
    ? `${t('task.flow.rule.branchScopeAny')} ${base}`
    : `${t('task.flow.rule.branchScopeSubset', { count: scope.ordinals.length, list: joinOrdinals(scope.ordinals, t) })} ${base}`;
}

/**
 * The scope tag for a banked block, with BOTH axes always named.
 *
 * Collapsing the "Any" halves was the shorter option and was rejected: an unqualified label would
 * then mean two different things depending on whether the block has a bank, and §8.2 of
 * `docs/question-versions.md` establishes this tag as load-bearing rather than decoration — it is
 * what stops "(Version 1) Chose Positive" being read as "everyone who chose Positive".
 *
 * A short form is used only where it is TRUE. Branch ordinals are compared across versions
 * because branch ids are fresh per version; where those ordinals differ, the parts are listed in
 * full rather than merged into a set product that describes cells which do not exist.
 */
function scopeLabel(scope: ConditionScope, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const { parts, versionTotal } = scope;
  if (parts.length === 0) return '';
  const everyVersion = parts.length === versionTotal;
  const noBranches = parts.every((p) => p.branchTotal === 0);
  // A lone version with no branches is not a scope worth naming — nothing was narrowed.
  if (everyVersion && noBranches && versionTotal <= 1) return '';

  const versionsFrag = everyVersion
    ? t('task.flow.rule.scopeAnyVersion')
    : t('task.flow.rule.scopeVersions', { count: parts.length, list: joinOrdinals(parts.map((p) => p.ordinal), t) });

  if (noBranches) return t('task.flow.rule.scopeWrap', { inner: versionsFrag });

  const pair = (branches: string) =>
    t('task.flow.rule.scopeWrap', { inner: t('task.flow.rule.scopePair', { versions: versionsFrag, branches }) });

  // Every contributing version contributed all of ITS branches, however many each has.
  if (parts.every((p) => p.branchTotal === 0 || p.branchOrdinals.length === p.branchTotal)) {
    return pair(t('task.flow.rule.scopeAnyBranch'));
  }
  // The same branch positions in each — true for versions duplicated from one another.
  const firstKey = parts[0]!.branchOrdinals.join(',');
  if (parts.every((p) => p.branchOrdinals.join(',') === firstKey)) {
    return pair(t('task.flow.rule.scopeBranches', {
      count: parts[0]!.branchOrdinals.length,
      list: joinOrdinals(parts[0]!.branchOrdinals, t),
    }));
  }

  const listed = parts.map((p) => {
    const version = t('task.flow.rule.scopeVersions', { count: 1, list: String(p.ordinal) });
    if (p.branchTotal === 0) return version;
    const branches = p.branchOrdinals.length === p.branchTotal
      ? t('task.flow.rule.scopeAnyBranch')
      : t('task.flow.rule.scopeBranches', { count: p.branchOrdinals.length, list: joinOrdinals(p.branchOrdinals, t) });
    return `${version} ${branches}`;
  }).join(t('task.flow.rule.scopePartSeparator'));
  return t('task.flow.rule.scopeWrap', { inner: listed });
}

/** Every reason a state can be currently unreachable — see inactiveReasonFor's doc comment.
 * Each has a matching `task.flow.rule.unreachableReason.<key>` i18n string. */
export type InactiveReasonKey =
  | 'mathErrorsOff' | 'mathErrorCategoryOff' | 'mathErrorUnreachable' | 'unitsOff' | 'sigFigsOff' | 'notationOff'
  | 'uncertaintyOff'
  // Reachability on the value-range blocks (gps/compass/angle/slider). Both are consequences of
  // the ANSWER SETUP, not of a feedback edit, which is exactly why they belong here: an answer
  // change that merely moves a target stays deliberately quiet (a teacher's own business), but one
  // that makes a state impossible to reach silently breaks any Flow rule aiming at it.
  | 'defaultIncorrectUnreachable' | 'feedbackSubsumedByCorrect'
  // Grading mode gates evaluation before anything block-specific runs, so it makes whole classes
  // of state unreachable regardless of block type — see gradingModeInactiveReason.
  | 'gradingOptional' | 'gradingSurvey'
  // Set Answer is the one targeted trigger a MODE can kill: it exists only outside graded.
  | 'gradingGraded';

/**
 * Some block-specific extra/targeted states exist on every block of that type but only
 * actually fire when the teacher has turned the relevant check on — e.g. Numerical's
 * sig-fig-error state is inert unless `sigFigsRequired` is on, each `math_error_*` state is
 * inert unless `mathErrorEnabled` is on AND its own category hasn't been switched off, and every
 * per-unit `unit_*` targeted feedback is inert unless `unitMode` is actually `'withUnits'`
 * (turning units off doesn't delete that feedback text, it just makes it unreachable). An
 * inactive state can never fire, so it shouldn't clutter Any Incorrect (or Neutral) or a Special
 * event's option list as if it were a live possibility.
 *
 * A math-error category can also be toggled on yet still be unable to produce a distinguishable
 * wrong answer for the block's CURRENT formula (e.g. "Exponent" on a formula with no exponent to
 * misread) — numerical-block.tsx already computes this per-category "Active"/"Inactive" for its
 * own feedback editor and persists it as `mathErrorReachable` on close. When present, that's the
 * authoritative check; falls back to the coarser toggle-only check for a block saved before this
 * field existed, rather than hiding every math-error state until the teacher reopens that block.
 *
 * Returns the SPECIFIC reason a state is inactive (undefined = active) — a Special event that
 * targets one of these states can then explain exactly what changed, rather than just going
 * silently dead with no indication anything is wrong.
 */

/** Parses the editor's string-shaped numeric fields, which is also what gets persisted for every
 * value-range block (gps/compass/angle/slider all store `"25"`, not `25`). Mirrors each block
 * component's own local `num()`. */
function flowNum(v: unknown): number {
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

interface RangeAnswerShape { id?: string; value?: unknown; toleranceValue?: unknown; toleranceAbove?: unknown; toleranceBelow?: unknown }
interface RangeBetweenShape { id?: string; low?: unknown; high?: unknown }

/** The accepted (Correct) intervals for a value-range block, computed from PERSISTED data so Flow
 * sees exactly what a student would be graded against. Returns null for block types that have no
 * interval notion, which is what keeps the caller's per-type switch honest. */
function acceptedRangesForFlow(block: Block): AcceptedRangeShape[] | null {
  const d = block.data as Record<string, unknown>;
  const answers = ((d.answers as RangeAnswerShape[] | undefined) ?? []).map(a => ({
    id: String(a.id ?? ''), value: flowNum(a.value),
    toleranceValue: flowNum(a.toleranceValue), toleranceAbove: flowNum(a.toleranceAbove), toleranceBelow: flowNum(a.toleranceBelow),
  }));
  const betweenAnswers = ((d.betweenAnswers as RangeBetweenShape[] | undefined) ?? []).map(a => ({
    id: String(a.id ?? ''), low: flowNum(a.low), high: flowNum(a.high),
  }));
  const toleranceSymmetry = d.toleranceSymmetry === 'asymmetric' ? 'asymmetric' as const : 'symmetric' as const;

  switch (block.type) {
    case 'gps': {
      // GPS's accepted band is always "0 to tolerance metres" — one interval, or none when the
      // radius isn't usable yet (in which case nothing is subsumed and nothing is unreachable).
      const tol = flowNum(d.toleranceRadius);
      return tol > 0 ? [{ lo: 0, hi: tol, loInclusive: true, hiInclusive: true }] : [];
    }
    case 'compass':
      // exact | between only — no thresholds, so every boundary is inclusive.
      return computeCompassAcceptedRanges({
        conditionType: d.conditionType === 'between' ? 'between' : 'exact',
        answers, betweenAnswers, toleranceSymmetry,
      }).map(r => ({ ...r, loInclusive: true, hiInclusive: true }));
    case 'rotation':
      return computeRotationAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
        answers, betweenAnswers, toleranceSymmetry, threshold: flowNum(d.threshold),
      });
    case 'tilt':
      // Same shape as Rotation's, over a fixed -90..90 instead of a 0..360 loop — no `toleranceMode`,
      // because a percentage of a lean is as meaningless as a percentage of a bearing.
      return computeTiltAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
        answers, betweenAnswers, toleranceSymmetry, threshold: flowNum(d.threshold),
      });
    case 'slider':
      return computeSliderAcceptedRanges({
        conditionType: (d.conditionType as 'exact' | 'between' | 'lt' | 'lte' | 'gt' | 'gte') ?? 'exact',
        answers, betweenAnswers,
        toleranceMode: (d.toleranceMode as 'absolute' | 'percentage') ?? 'absolute',
        toleranceSymmetry, threshold: flowNum(d.threshold),
      });
    default:
      return null;
  }
}

/** Whether a value-range block's Default Incorrect has nothing left to catch, or a targeted state
 * sits entirely inside the accepted band (so Correct always wins first). Each block type keeps its
 * own predicate because the interval algebra differs — Compass wraps at the 0/360 seam, GPS is a
 * single half-open band from zero, Angle is a closed loop, Slider is bounded by its own min/max.
 *
 * WHICH predicate is no longer decided here. This used to be two four-way ternaries, one per
 * question, and numerical was in neither — it fell through to slider's, which happens to be correct
 * (an unprefixed trigger round-trips through slider's handle parser unchanged) but only by
 * coincidence, and a sixth block type would have fallen through the same way rather than failing.
 * `valueDefaultIncorrectUnreachable` and `valueFeedbackSubsumedByCorrect` hold that dispatch beside
 * the evaluators, as exhaustive switches with no default arm. */
/** The block's grading mode, defaulting as every other reader here does. */
function mode(block: Block): string {
  return (block.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded';
}

function rangeBlockInactiveReason(block: Block, state: FeedbackStateShape): InactiveReasonKey | undefined {
  const ranges = acceptedRangesForFlow(block);
  if (ranges === null || !isValueRangeBlockType(block.type)) return undefined;
  // Raw persisted states, not namedStatesFor — that filters by isActiveForFlow, which calls back
  // into this very function, and the coverage maths needs EVERY authored targeted trigger anyway.
  const triggers = feedbackStatesOf(block).filter(s => s.type === 'targeted').map(s => s.trigger);

  if (state.trigger === 'incorrect') {
    return valueDefaultIncorrectUnreachable(block.type, ranges, triggers)
      ? 'defaultIncorrectUnreachable' : undefined;
  }

  if (state.type === 'targeted') {
    /*
     * "Superseded by the correct answer" is a claim about CORRECT, which only graded has.
     *
     * Outside graded this hid a state that genuinely fires: Correct is never checked, so nothing
     * supersedes anything, and a Set Answer card is explicitly NOT terminal — a targeted
     * condition inside the accepted band fires right alongside it. Flow was dropping it from the
     * picker, so the one rule a teacher wants there ("they gave the answer AND said this") could
     * not be written, with nothing on screen to say why the state was missing.
     *
     * Same mistake, third place: the per-card warning and the block's issues list both applied
     * band reasoning on a mode with no band, and were fixed. This copy is Flow's.
     */
    if (mode(block) !== 'graded') return undefined;
    return valueFeedbackSubsumedByCorrect(block.type, state.trigger, ranges)
      ? 'feedbackSubsumedByCorrect' : undefined;
  }

  return undefined;
}


/** States that survive a non-graded mode. In Participation, evaluate() checks "no answer" and the
 * sensor accuracy gates BEFORE the survey branch, so those still fire; only correctness-dependent
 * states are dead. In Optional the any_response branch short-circuits first, so literally nothing
 * else fires — not even No Answer. */
const SURVIVES_SURVEY = new Set([
  'unanswered', 'incomplete', 'any_answer',
  // `set_answer` is a targeted trigger like any other and is covered by the `type === 'targeted'`
  // escape in `gradingModeInactiveReason`; it is named here only so a reader looking for it finds
  // the reason rather than its absence.
  SET_ANSWER_TRIGGER,
  // Every sensor state, and the rule is the evaluator's own order rather than a judgement: each of
  // these gates ABOVE the survey branch in evaluateGPS/Compass/Angle, so each still fires when
  // correctness no longer does. `no_sensor_data` and `poor_axis` were missed when they were added,
  // which marked them inactive on any sensor block in Participation — so Flow offered them as
  // conditions nowhere and a rung that catches them looked empty.
  NO_SENSOR_DATA_TRIGGER,
  'poor_accuracy', 'poor_heading_accuracy', 'poor_position_accuracy', 'poor_axis',
  /*
   * Numerical's FORM faults, for the same reason as the sensor gates above: each is decided
   * before `evaluateNumerical` reaches anything about correctness, so each still fires when the
   * verdict no longer does. Whether a string is in scientific notation, how many significant
   * figures it carries and whether its unit is one the question takes are all answerable with
   * no correct answer.
   *
   * Whether the teacher ASKED for any of them is a separate question, and the numerical arm of
   * `inactiveReasonFor` already answers it below — `notationOff`, `sigFigsOff`, `unitsOff`.
   * This list only decides what the MODE kills.
   *
   * `math_error_*` is deliberately absent: its candidates come from mutating the correct
   * formula, so it diagnoses a relation to a reference participation does not yet have. It
   * joins when Set Answer does.
   */
  'notation_error', 'sigfig_error', 'unit_error',
  /*
   * The error box's FORM faults, which belong here for the same reason the two above do: whether
   * the uncertainty was rounded as asked, and whether the value's precision contradicts it, are
   * both true of the two strings alone. `9.81234 ± 0.3` is inconsistent whether or not 9.8 is
   * right, so participation can say so.
   *
   * The uncertainty's own correctness is deliberately absent, the way `math_error_*` is: it is
   * judged against an answer key, and participation has none. A wrong uncertainty there falls to
   * Default Incorrect like any other wrong answer.
   */
  'agreement_error', 'uncertainty_sigfig_error',
]);

/**
 * Whether a state is dead because of the block's GRADING MODE rather than anything block-specific.
 * Uniform across every block type: each evaluate() gates on gradingMode before it touches its own
 * answer logic (verified across mcq/ordering/keyword/slider/numerical/gps/compass/angle), so this
 * needs no per-type knowledge at all.
 *
 * Fixes a real bug: a block switched to Optional or Participation still offered its Default
 * Incorrect and every targeted state as Flow conditions, none of which can ever fire again. A rule
 * built on one of those looks configured and silently never runs.
 *
 * Deliberately does NOT flag any_response/any_answer as dead in graded mode. They're the positive
 * slot, `namedStatesFor` already excludes positive triggers from the picker, and `positiveStateOf`
 * (the All Correct card) reads them directly without an activity filter — reporting them inactive
 * would risk emptying that card for no benefit.
 */
function gradingModeInactiveReason(block: Block, state: FeedbackStateShape): InactiveReasonKey | undefined {
  const mode = (block.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded';
  // Optional is INERT — one catch-all and nothing else can fire. See `targetedFiresIn`.
  if (mode === 'optional') return state.trigger === 'any_response' ? undefined : 'gradingOptional';
  /*
   * SET ANSWER DOES NOT EXIST IN GRADED, and Flow was offering it there.
   *
   * `targetedFiresIn` refuses it in graded unconditionally — the accepted band IS `correct`
   * there, so the preset would be a second name for a card Flow already has. But this function
   * only ever asked questions about survey and optional, and returned "active" for everything
   * in graded, so a block switched to graded kept offering the Set Answer card it had been
   * given in Participation. A rule built on it looks configured and can never run.
   */
  if (state.trigger === SET_ANSWER_TRIGGER) return mode === 'graded' ? 'gradingGraded' : undefined;
  if (mode !== 'survey') return undefined;
  /*
   * TARGETED states fire in survey, and this said they did not.
   *
   * `SURVIVES_SURVEY` lists DEFAULT triggers, and every targeted trigger fell off the end of it
   * and was reported mode-dead — so Flow offered none of them as conditions. Harmless while a
   * participation block could not fire one; load-bearing now that splitting a block is what takes
   * All Correct off the table, because the events are then the only way out and a teacher could
   * not write one.
   */
  if (state.type === 'targeted') return undefined;
  return SURVIVES_SURVEY.has(state.trigger) ? undefined : 'gradingSurvey';
}

export function inactiveReasonFor(block: Block, state: FeedbackStateShape): InactiveReasonKey | undefined {
  // Grading mode first: it gates evaluate() before any block-specific logic, so a mode-dead state
  // should say so rather than reporting some downstream reachability detail that never applies.
  const modeReason = gradingModeInactiveReason(block, state);
  if (modeReason) return modeReason;

  // Was `if (block.type !== 'numerical') return undefined` — every non-numerical block's
  // reachability was invisible to Flow, so a rule could keep firing on a state that the answer
  // setup had made impossible (GPS's Default Incorrect going unreachable once every distance has
  // its own targeted feedback; a targeted state swallowed by a widened tolerance). The block
  // editors already detected both and warned; Flow didn't.
  if (block.type !== 'numerical') return rangeBlockInactiveReason(block, state);
  const data = block.data as {
    sigFigsRequired?: boolean; requireExponentialNotation?: boolean;
    mathErrorEnabled?: boolean; mathErrorCategories?: Record<string, boolean>;
    mathErrorReachable?: Record<string, boolean>; unitMode?: string;
    uncertaintyRequired?: boolean; integerOnly?: boolean;
    conditionType?: NumericalConditionType;
  };
  if (state.trigger === 'notation_error') return data.requireExponentialNotation === true ? undefined : 'notationOff';
  if (state.trigger === 'sigfig_error') return data.sigFigsRequired === true ? undefined : 'sigFigsOff';
  /* The whole family at once, including the math-error cards, because they all address the
     second box, and `uncertaintyAsked` is what decides whether the question has one. A rung
     routing on a card nothing can reach is the thing this function exists to say. */
  if (state.trigger === 'agreement_error' || state.trigger.startsWith('uncertainty_')) {
    return uncertaintyAsked(data) ? undefined : 'uncertaintyOff';
  }
  if (state.trigger.startsWith('math_error_')) {
    if (data.mathErrorEnabled !== true) return 'mathErrorsOff';
    const category = state.trigger.slice('math_error_'.length);
    if (data.mathErrorCategories?.[category] === false) return 'mathErrorCategoryOff';
    if (data.mathErrorReachable?.[category] === false) return 'mathErrorUnreachable';
    return undefined;
  }
  if (state.trigger.startsWith('unit_')) return data.unitMode === 'withUnits' ? undefined : 'unitsOff';
  return undefined;
}

function isActiveForFlow(block: Block, state: FeedbackStateShape): boolean {
  return inactiveReasonFor(block, state) === undefined;
}

/**
 * Numerical's own feedback panel always groups its extra/targeted states into a fixed section
 * order — Math Error, Unit Feedback, Value Feedback, Sig Figs, then Default Incorrect last —
 * regardless of what order they happen to be stored in (a block saved before some reordering, or
 * whose targeted states were added in a different sequence, still keeps whatever raw order is in
 * `block.data.feedback.states`). Flow reads that raw array directly rather than going through the
 * block's own normalizeFeedback(), so it needs this same category ordering applied explicitly —
 * otherwise Flow's chip list can show, say, Sig Figs before Math Error just because that's how an
 * older save happened to store it. Stable-sorts (JS Array#sort is stable) so within any one
 * category, whatever relative order already existed is preserved.
 */
function numericalCategoryPriority(trigger: string): number {
  /* The error box's cards sit with their counterparts for the value. There is no separate
     `uncertainty_math_error_*` rung any more: the seven categories are one shared set of cards fed
     by two equations, so a sign flip ranks the same whichever number it was found in. */
  if (trigger === 'uncertainty_sigfig_error' || trigger === 'agreement_error') return 3;
  if (trigger.startsWith('uncertainty_')) return 2; // targeted traps on the error box
  if (trigger.startsWith('math_error_')) return 0;
  if (trigger.startsWith('unit_')) return 1;
  if (trigger === 'notation_error') return 3;
  if (trigger === 'sigfig_error') return 3;
  if (trigger === 'incorrect') return 4;
  return 2; // targeted value feedback (non-unit)
}

function sortForDisplay(block: Block, states: FeedbackStateShape[]): FeedbackStateShape[] {
  if (block.type !== 'numerical') return states;
  return [...states].sort((a, b) => numericalCategoryPriority(a.trigger) - numericalCategoryPriority(b.trigger));
}

/**
 * States a Special event can target on this block: its Default Incorrect state, any
 * block-specific extra state (Numerical's math-error/sig-fig traps, etc.), any targeted feedback
 * state, AND the positive slot. Only the retry states are withheld — those belong to the Any
 * Incomplete card. Inactive extra states (a math-error check the teacher hasn't turned on) are
 * excluded: nothing a teacher could not actually trigger should be offered as a target.
 *
 * ## The positive slot used to be conditional, and that was a trap
 *
 * It was hidden unless `allCorrectInert`, on the argument that All Correct owned it and offering
 * `correct` would duplicate the card already catching it. That argument rests on All Correct
 * being a test about ONE block, and it is not: it asks whether EVERY block passed. A condition
 * on one block's pass state is strictly finer, and `[MCQ1 = any_answer] AND [MCQ2 = chose left]`
 * cannot be written by the card at all.
 *
 * The two coincide only on a task with a single gradable block, where the panel's own duplicate
 * and contradiction detection can say so — which is the right place for a redundancy to be
 * reported rather than prevented.
 *
 * It also had to stop being conditional BEFORE All Correct becomes a positive test, not after.
 * `allCorrectInert` is `splitParticipationBlocks(blocks).size > 0`, and that machinery goes away
 * with the positive test — so the gate would have been permanently false, and routing on "any
 * other answer" would have become impossible in the gap.
 */
export function namedStatesFor(block: Block): FeedbackStateShape[] {
  return sortForDisplay(block, feedbackStatesOf(block).filter(
    (s) => !isRetryTrigger(s.trigger) && isActiveForFlow(block, s),
  ));
}


/** Every state on this block that means "not enough of an answer to grade yet" — No Answer,
 * Incomplete Answer, and any sensor "poor accuracy" gate (GPS/Compass) — the "Any Incomplete"
 * card's row for this block. Mirrors flow.ts's isRetryTrigger so the UI and the resolution
 * engine never disagree about what counts as a retry. */
export function retryStatesOf(block: Block): FeedbackStateShape[] {
  // isActiveForFlow, like every other collection here. Without it the Any Incomplete card kept
  // listing No Answer for an Optional block — but Optional short-circuits to any_response before
  // evaluate() ever checks for an answer, so "incomplete" isn't a state that block can reach: not
  // answering IS a valid response there.
  return feedbackStatesOf(block).filter((s) => isRetryTrigger(s.trigger) && isActiveForFlow(block, s));
}

/**
 * Every state on this block that means "the STUDENT hasn't answered yet" — No Answer, Incomplete
 * Answer. The Any Incomplete rung's row for this block.
 *
 * Split from retryStatesOf, which lumped these together with the sensor gates below. The two rungs
 * are now separate and answer to different teachers' decisions, so the two collections are too.
 */
export function incompleteStatesOf(block: Block): FeedbackStateShape[] {
  return feedbackStatesOf(block).filter((s) => isIncompleteTrigger(s.trigger) && isActiveForFlow(block, s));
}

/** Every state on this block meaning the device produced NO reading — the No Sensor Data rung. */
export function noSensorDataStatesOf(block: Block): FeedbackStateShape[] {
  return feedbackStatesOf(block).filter((s) => isNoSensorDataTrigger(s.trigger) && isActiveForFlow(block, s));
}

/** Every state meaning the reading arrived but is too poor to grade — the Poor Sensor Data rung. */
export function poorSensorDataStatesOf(block: Block): FeedbackStateShape[] {
  return feedbackStatesOf(block).filter((s) => isPoorSensorDataTrigger(s.trigger) && isActiveForFlow(block, s));
}

/** Either kind, for callers that only ask whether the hardware can fail this block at all. */
export function sensorStatesOf(block: Block): FeedbackStateShape[] {
  return feedbackStatesOf(block).filter((s) => isSensorTrigger(s.trigger) && isActiveForFlow(block, s));
}

/**
 * The blocks a sensor rung speaks for, in task order.
 *
 * Defined by what a block can FIRE, not by its type: a GPS block whose accuracy gate is switched
 * off can't report a sensor problem, so asking the teacher what to do about one would be asking
 * about something that can't happen. Whatever block type gains a sensor gate next lands here with
 * no list to update — which is also how the No Sensor Data rung stays hidden until the blocks grow
 * a state for it, rather than sitting on screen inert.
 */
/**
 * THE STARVED BLOCKS STILL WORTH SAYING SOMETHING ABOUT.
 *
 * `starvedBlockCauses` says which blocks make All Correct unreachable. This subtracts the ones
 * the teacher has already accepted, which is what the panel and the button's dot both actually
 * want — a splitter that routes every option by rule starves the rung BY DESIGN, and a warning
 * that cannot be put down is one people learn to scroll past.
 *
 * Both callers go through here rather than each remembering to subtract: the dot and the card
 * disagreeing about whether there is a problem is precisely the bug the dot was added to fix.
 */
export function unacceptedStarvedBlocks(
  blocks: Block[], flow?: { starvedAccepted?: string[] } | null,
): ReturnType<typeof starvedBlockCauses> {
  const accepted = new Set(flow?.starvedAccepted ?? []);
  return starvedBlockCauses(blocks).filter((b) => !accepted.has(b.id));
}

export function noSensorDataBlocks(blocks: Block[]): Block[] {
  return blocks.filter((b) => noSensorDataStatesOf(b).length > 0);
}

export function poorSensorDataBlocks(blocks: Block[]): Block[] {
  return blocks.filter((b) => poorSensorDataStatesOf(b).length > 0);
}

export function sensorBlocks(blocks: Block[]): Block[] {
  return blocks.filter((b) => sensorStatesOf(b).length > 0);
}

/**
 * A feedback CARD a Flow rung names, as opposed to an ANSWER a card fires on.
 *
 * Two different things wearing the same clothes was the problem: the tone-filled pill with a bare
 * check/cross/dash means "this answer", and using it for "this card" made Flow look like it was
 * matching answers. A card is named by a bubble glyph carrying the same mark inside it.
 */
export interface FlowCardRef {
  /** Stable across renders and unique within a list: trigger plus evaluation. */
  key: string;
  label: string;
  evaluation: FeedbackStateShape['evaluation'];
}

/** The generic name of a category of default state, independent of what any one block calls it. A
 * rung speaks about the task, so it uses the category's name, not one block's edited title. */
const DEFAULT_TITLE_KEYS: Record<string, string> = {
  unanswered: 'blocks.feedbackDefaults.noAnswerTitle',
  incomplete_answer: 'blocks.feedbackDefaults.incompleteAnswerTitle',
  correct: 'blocks.feedbackDefaults.correctTitle',
  any_answer: 'blocks.feedbackDefaults.anyAnswerTitle',
  any_response: 'blocks.feedbackDefaults.anyResponseTitle',
  // The sensor categories. Their titles are locked on the block, so the stored one would do — but
  // going through the map keeps ONE derivation for what a rung calls a card, and a rung that read
  // some names from a map and others off the block is how those two drift.
  [NO_SENSOR_DATA_TRIGGER]: 'blocks.sensors.noSensorDataFeedback',
  poor_accuracy: 'blocks.gps.poorAccuracyFeedback',
  poor_heading_accuracy: 'blocks.compass.poorHeadingAccuracyFeedback',
  poor_position_accuracy: 'blocks.compass.poorPositionAccuracyFeedback',
  // The error box's two FORM faults. Named here rather than left to fall through to the stored
  // title, for the reason stated above: a rung that read some names from a map and others off the
  // block is how the two drift. The math-error categories are deliberately absent — they carry the
  // teacher's wording and have no fixed category name — and so is the uncertainty's correctness,
  // which has no card of its own and falls to Default Incorrect.
  uncertainty_sigfig_error: 'blocks.numerical.uncertaintySigfigFeedback',
  agreement_error: 'blocks.numerical.agreementErrorFeedback',
};

/**
 * `poor_axis`, which two blocks raise and name differently.
 *
 * Every other default category means one thing wherever it appears, so a trigger alone picks its
 * title. This one does not: Rotation's card is "Off Axis" (the gesture left the plane it was
 * calibrated in) and Tilt's is "Held at an Angle" (the phone sat diagonally between its two
 * edges). Those are different mistakes with different remedies, and a rung that called both by
 * Rotation's name — which is what the shared map did — told a teacher with a Tilt block about a
 * calibration plane their block does not have.
 *
 * Keyed on the block, therefore, and only for this trigger. Collapsing the two into one shared
 * string the way `no_sensor_data` is shared was the tempting fix and is the wrong one: that
 * category genuinely says one thing on all three blocks, and this one does not.
 */
const POOR_AXIS_TITLE_KEYS: Record<string, string> = {
  rotation: 'blocks.rotation.poorAxisFeedback',
  tilt: 'blocks.tilt.poorAxisFeedback',
};

/** The locale key a default state's category is named by, given the block that raised it. */
function defaultTitleKey(trigger: string, blockType?: string): string | undefined {
  if (trigger === 'poor_axis') return blockType ? POOR_AXIS_TITLE_KEYS[blockType] : undefined;
  return DEFAULT_TITLE_KEYS[trigger];
}

/** Reading order within a rung: the categories in the order a teacher meets them, and within one
 * category the stricter evaluation first. A state with no category (a targeted one, whose title is
 * the teacher's) sorts after the defaults, in the order the blocks list it. */
const TRIGGER_ORDER = [
  NO_SENSOR_DATA_TRIGGER, 'unanswered', 'incomplete_answer',
  'poor_accuracy', 'poor_heading_accuracy', 'poor_position_accuracy', 'poor_axis',
  'correct', 'any_answer', 'any_response',
];
const EVAL_ORDER: FeedbackStateShape['evaluation'][] = ['incorrect', 'neutral', 'correct'];

interface CardEntry {
  label: string;
  evaluation: FeedbackStateShape['evaluation'];
  /** Position in TRIGGER_ORDER, or a large number for a state with no fixed category. */
  rank: number;
}

/**
 * One entry per DISTINCT (name, evaluation) across the task's gradable blocks.
 *
 * Distinct rather than aggregated, because blocks can disagree: one block may treat a blank as
 * incorrect while another leaves it neutral. Collapsing to the strictest would misreport every
 * block that does not, and inventing a "mixed" mark would need a symbol that says it varies without
 * saying which way. Two entries with the same name is what is actually true, and it looks odd
 * exactly when the setup is odd; where the blocks agree, which is the ordinary case, there is one.
 */
function cardRefsFrom(entries: CardEntry[]): FlowCardRef[] {
  const seen = new Map<string, FlowCardRef & { rank: number }>();
  for (const e of entries) {
    const key = `${e.label}:${e.evaluation}`;
    if (!seen.has(key)) seen.set(key, { key, label: e.label, evaluation: e.evaluation, rank: e.rank });
  }
  return [...seen.values()]
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank
      : EVAL_ORDER.indexOf(a.evaluation) - EVAL_ORDER.indexOf(b.evaluation)))
    .map(({ key, label, evaluation }) => ({ key, label, evaluation }));
}

/** A default state named by its CATEGORY rather than by one block's edited title — a rung speaks
 * about the task, so "No Answer" means every block's No Answer. */
function defaultEntry(state: FeedbackStateShape, t: (key: string) => string, blockType?: string): CardEntry | null {
  const titleKey = defaultTitleKey(state.trigger, blockType);
  if (!titleKey) return null;
  return { label: t(titleKey), evaluation: state.evaluation, rank: TRIGGER_ORDER.indexOf(state.trigger) };
}

/** What the Any Incomplete rung catches, as cards. Empty where no block can be incomplete. */
export function incompleteCardRefs(blocks: Block[], t: (key: string) => string): FlowCardRef[] {
  return cardRefsFrom(gradableBlocks(blocks)
    .flatMap((b) => incompleteStatesOf(b).flatMap((s) => defaultEntry(s, t, b.type) ?? [])));
}

/** What the No Sensor Data rung catches, as cards. Empty until a block can report one. */
export function noSensorDataCardRefs(blocks: Block[], t: (key: string) => string): FlowCardRef[] {
  return cardRefsFrom(gradableBlocks(blocks)
    .flatMap((b) => noSensorDataStatesOf(b).flatMap((s) => defaultEntry(s, t, b.type) ?? [])));
}

/** What the Poor Sensor Data rung catches, as cards. */
export function poorSensorDataCardRefs(blocks: Block[], t: (key: string) => string): FlowCardRef[] {
  return cardRefsFrom(gradableBlocks(blocks)
    .flatMap((b) => poorSensorDataStatesOf(b).flatMap((s) => defaultEntry(s, t, b.type) ?? [])));
}

/**
 * What ONE block contributes to a sensor rung, as cards.
 *
 * For a split row, which names a single sensor: "GPS 1 is [Low Accuracy]". The consolidated card
 * says the same thing across every sensor at once, so both go through `cardRefsFrom` and a row and
 * the card above it can never disagree about what a state is called.
 */
export function sensorCardRefsForBlock(
  block: Block,
  kind: 'noData' | 'poorData',
  t: (key: string) => string,
): FlowCardRef[] {
  const states = kind === 'noData' ? noSensorDataStatesOf(block) : poorSensorDataStatesOf(block);
  return cardRefsFrom(states.flatMap((s) => defaultEntry(s, t, block.type) ?? []));
}

/** What All Correct means, as cards: every block's own positive state — Correct on a graded block,
 * Any Answer or Any Response on one that is not. */
export function positiveCardRefs(blocks: Block[], t: (key: string) => string): FlowCardRef[] {
  return cardRefsFrom(gradableBlocks(blocks)
    .flatMap((b) => { const p = positiveStateOf(b); return p ? defaultEntry(p, t, b.type) ?? [] : []; }));
}

/**
 * The states an event PROVABLY intercepts, so they can never reach Default.
 *
 * Only two shapes can be proved, and the proof is about whether the feedback condition alone
 * decides the match:
 *
 *   - `matchMode: 'any'` — one satisfied condition fires the event. So every feedback state it
 *     names is intercepted, EVEN with an attempts condition beside it: under `any`, that attempts
 *     condition can add matches but can never withhold one.
 *   - `matchMode: 'all'` with exactly ONE condition, and that condition a feedback one. Nothing
 *     else has to hold, so the state firing is the event firing.
 *
 * Everything else stays unproven, which is the case worth being careful about: `all` with a
 * feedback condition AND `attempts > 5` fires only sometimes, so that state reaches Default on the
 * submissions where the count has not got there yet. Claiming interception would be wrong on
 * exactly the laps a teacher is trying to reason about.
 *
 * A PAUSED event intercepts nothing: `resolveFlow` skips a drifted rule and a contradictory one can
 * never satisfy its own `all`, so both fall past to Default like an event that was never written.
 */
export function interceptedStateKeys(rules: FlowRule[], blocks: Block[]): Set<string> {
  const keys = new Set<string>();
  for (const rule of rules) {
    if (isRulePaused(rule, blocks)) continue;
    const feedback = rule.conditions.filter((c): c is Extract<FlowCondition, { kind: 'feedback' }> => c.kind === 'feedback');
    const decidesAlone = rule.matchMode === 'any' || rule.conditions.length === 1;
    if (!decidesAlone) continue;
    for (const c of feedback) keys.add(`${c.blockId}::${c.stateId}`);
  }
  return keys;
}

/**
 * What can still reach the Default rung: each block's Default Incorrect, plus every named incorrect
 * or neutral state — minus the ones an event provably intercepts.
 *
 * The filtering was absent, and deliberately so: when conditions were a grid the old rule ("this
 * state alone satisfies the only block in the rule") stopped being sound once the flat list let an
 * attempts condition sit beside a feedback one. That reasoning was right about the hard case and
 * gave up on the easy ones — `interceptedStateKeys` proves the two shapes that can be proved and
 * stays silent on the rest, so the list is exact where it can be and over-inclusive where it must
 * be, rather than over-inclusive always.
 *
 * An EMPTY result with events authored is a collision: every outcome is spoken for and Default can
 * never fire. Worth saying, because an event that quietly swallows the catch-all is invisible.
 */
export function fallthroughCardRefs(
  blocks: Block[],
  rules: FlowRule[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale?: string,
): FlowCardRef[] {
  const intercepted = interceptedStateKeys(rules, blocks);
  return cardRefsFrom(gradableBlocks(blocks).flatMap((b) => incorrectOrNeutralStatesOf(b)
    .filter((s) => !intercepted.has(`${b.id}::${s.id}`))
    .map((s) => ({
      label: flowStateLabel(s, t, b, locale),
      evaluation: s.evaluation,
      // Default Incorrect last: it is the catch-all the others fall past, and reading it first
      // suggests the named states are exceptions to it rather than the other way round.
      rank: s.trigger === 'incorrect' ? 100 : 50,
    }))));
}

const LOW_ACCURACY_TRIGGERS = new Set(['poor_accuracy', 'poor_heading_accuracy', 'poor_position_accuracy']);

/** Whether any gradable block in this task actually has an Incomplete Answer state — the Any
 * Incomplete card's collapsed summary only mentions the categories that could actually fire. */
export function hasIncompleteAnswerAnywhere(blocks: Block[]): boolean {
  return gradableBlocks(blocks).some((b) => retryStatesOf(b).some((s) => s.trigger === 'incomplete_answer'));
}

/** Whether any gradable block in this task has a sensor "poor accuracy" gate (GPS/Compass) —
 * same purpose as hasIncompleteAnswerAnywhere, for the Low Accuracy category. */
export function hasLowAccuracyAnywhere(blocks: Block[]): boolean {
  return gradableBlocks(blocks).some((b) => retryStatesOf(b).some((s) => LOW_ACCURACY_TRIGGERS.has(s.trigger)));
}

/** Every state that would satisfy the "Any Incorrect (or Neutral)" card for this block — its
 * Default Incorrect state, any specific named state currently marked incorrect, AND any
 * neutral-evaluation state (e.g. Numerical's sign-flip/sig-fig traps). Excludes retry states
 * (handled by Any Incomplete instead) and inactive extra states (a math-error check the teacher
 * hasn't turned on can never actually fire). */
export function incorrectOrNeutralStatesOf(block: Block): FeedbackStateShape[] {
  return sortForDisplay(block, feedbackStatesOf(block).filter(
    (s) => !isRetryTrigger(s.trigger) && s.evaluation !== 'correct' && isActiveForFlow(block, s),
  ));
}

/**
 * This block's "positive" default state — whatever it's actually called for its current grading
 * mode (Correct / Any Answer / Any Response). There is no state literally named "Neutral"; that
 * was a display-layer invention. Matched by an explicit trigger-name inclusion list (see
 * POSITIVE_TRIGGERS) rather than by exclusion — an exclusion-based filter would wrongly match
 * whichever block-specific extra default state (e.g. Numerical's sigfig_error) happens to sit
 * earlier in the stored states array.
 */
export function positiveStateOf(block: Block): FeedbackStateShape | undefined {
  // Mode-aware, not first-match. A block carries leftover positive-slot states from whatever modes
  // it has previously been in — switching to Optional doesn't delete the graded `correct` state —
  // so a bare `.find()` over POSITIVE_TRIGGERS returned whichever happened to sit earliest in the
  // array. Live symptom: a GPS block set to Optional showed its graded Correct chip ("Within 25 m
  // of 40.7128, -74.006") in Flow's All Correct card, when only Any Response can fire in that mode.
  // Prefer the trigger the block's CURRENT mode actually evaluates, and only fall back to any
  // positive state when that one hasn't been created yet.
  const states = feedbackStatesOf(block);
  const mode = (block.data as { gradingMode?: string } | undefined)?.gradingMode ?? 'graded';
  const wanted = mode === 'optional' ? 'any_response' : mode === 'survey' ? 'any_answer' : 'correct';
  return states.find((s) => s.type === 'default' && s.trigger === wanted)
    ?? states.find((s) => s.type === 'default' && isPositiveTrigger(s.trigger));
}

/** True if a block's data shape carries a feedback-states list at all (excludes text/image/sound/dataviz). */
export function blockHasFeedback(block: Block): boolean {
  return Array.isArray((block.data as { feedback?: { states?: unknown } } | undefined)?.feedback?.states);
}

/**
 * Blocks that can actually produce a FeedbackState — the universe the three built-in Flow cards
 * (Missing/Incomplete, Default Incorrect, Correct) check against. Display blocks
 * (text/image/sound/dataviz) never fire an evaluation, so they're excluded.
 *
 * BY TYPE, not by what has been SAVED. This read `blockHasFeedback`, which asks whether a block
 * has written its feedback states yet — and a block seeds those at render through
 * `normalizeFeedback`, not on creation. So every block a teacher had merely ADDED was missing
 * from this universe, and with it from every card built on it: All Correct drew an empty token
 * list, which is how the owner met it — "a note that vanishes and it just looks like it's
 * broken". Across the dev database that is about sixty blocks of every type, ten of sixty-four
 * graded MCQs among them.
 *
 * `isGradableBlockType`'s own note already said to prefer it here, for this reason, in these
 * words: so Flow's events "appear the instant an input/sensor block is added, not only after its
 * first edit (which is what seeds the states `blockHasFeedback` looks for)".
 */
export function gradableBlocks(blocks: Block[]): Block[] {
  return blocks.filter(isGradableBlockType);
}

/** Block types that are pure content/display — they never produce a feedback evaluation. Everything
 * else (answerable inputs + sensors) is gradable. */
const DISPLAY_BLOCK_TYPES = new Set(['text', 'image', 'sound', 'dataviz']);

/** Whether a block's TYPE can be graded (an answerable input or a sensor) — independent of whether its
 * `feedback.states` have been seeded yet. Use this (not `blockHasFeedback`) to decide whether Flow's
 * events APPLY, so they appear the instant an input/sensor block is added, not only after its first
 * edit (which is what seeds the states `blockHasFeedback` looks for). */
export function isGradableBlockType(block: Block): boolean {
  return !!block.type && !DISPLAY_BLOCK_TYPES.has(block.type);
}


/**
 * Blocks some Change in this task actually touches.
 *
 * The On Return panel asks it: a "keep changes" switch on a block nothing changes does nothing,
 * and worse, it reads as a setting a teacher has to think about. A task with no Changes at all
 * has no such column.
 *
 * Reads the EFFECTS rather than the conditions. A change may be conditioned on block A and
 * apply to block B; it is B whose state survives or resets, and B is what this is about.
 */
export function blocksChangedBy(flow: Pick<TaskFlowConfig, 'changes'>): Set<string> {
  return new Set((flow.changes ?? []).flatMap((c) => (c.effects ?? []).map((e) => e.blockId)));
}

/** True if the task has any block that CAN be graded by type — the gate for showing Flow's events. */
export function taskHasGradableBlock(blocks: Block[]): boolean {
  return blocks.some(isGradableBlockType);
}

/**
 * WHETHER THIS TASK'S ROUTING HAS BEEN AUTHORED, as opposed to left where it was born.
 *
 * Only the Flow capability's gate asks. A teacher who has turned Flow off still reaches the panel
 * when the answer here is true, because the stored config keeps RUNNING either way and hiding it
 * would leave a task routing in a way nothing on screen explains and nothing can undo — the
 * trapdoor the version pager already had once. See `feedback_capability_gate_adoption_not_use`.
 *
 * ## Exits deliberately do NOT count
 *
 * Summary feedback is authored from this panel and is the one part a beginner keeps, so an exit is
 * exactly what a teacher with Flow off is expected to have. Counting it would unlock the whole
 * panel for every teacher who wrote "well done" at the end of a task, which is the opposite of
 * the intent.
 *
 * `onArrival` does not count either: it is offered only on a re-enterable task, which cannot exist
 * without a route back, which is itself authored routing. It would be true only when something
 * else here already is.
 */
export function flowIsAuthored(flow: TaskFlowConfig, throughLineTarget?: string | null): boolean {
  const stays = (a: FlowAction | undefined) => a == null || a.type === 'stay';
  /*
   * A SENSOR ROW THAT SAYS `stay` IS THE DEFAULT, not a decision.
   *
   * `normalizeFlowConfig` writes one row per sensor block the moment such a block exists, each
   * holding the rung's own `stay` — so counting rows at all made every task containing a GPS,
   * compass or tilt block look authored. Found on exactly that: a task with one compass, no
   * events, no changes, and two auto-written rows, which kept the full panel.
   *
   * What a teacher actually does here is split the rung open, or point a row somewhere. Both
   * survive; a list of rows all saying what the rung already said does not.
   */
  const rungAuthored = (r: SensorRung | undefined) =>
    r != null && (r.split || !stays(r.action) || (r.rules ?? []).some(row => !stays(row.action)));

  /*
   * A `goto` AT THE NEXT TASK IS THE THROUGH-LINE, not a decision.
   *
   * The board writes the spine's forward edge as an explicit `goto` rather than leaving it at
   * `next`, so counting every goto as authored grandfathered most of an ordinary linear
   * experience — measured against the dev database, 18 of 28 such tasks were pointing at nothing
   * more than the task directly below them. That is the state a task is BORN in once it has a
   * successor, and treating it as the teacher's own work meant the gate barely gated.
   *
   * `stay` still counts: severing the through-line is a decision, and it is the state most in
   * need of a way back.
   */
  const correct = flow.allCorrectAction;
  const correctAuthored = correct?.type === 'goto'
    ? !(throughLineTarget != null && correct.targetTaskId === throughLineTarget)
    : correct?.type !== 'next';

  return (flow.eventRules?.length ?? 0) > 0
    || (flow.changes?.length ?? 0) > 0
    || correctAuthored
    || !stays(flow.noAnswerAction)
    || !stays(flow.defaultAction)
    || rungAuthored(flow.noSensorData)
    || rungAuthored(flow.poorSensorData);
}

const BLOCK_LABEL_PREVIEW_MAX_LENGTH = 50;

/** Truncates with a trailing "..." only when actually cutting text short, matching this app's
 * standing rule against decorative ellipsis (see formula-plaintext.ts's truncateForLabel). */
function truncatePreview(text: string): string {
  if (text.length <= BLOCK_LABEL_PREVIEW_MAX_LENGTH) return text;
  return `${text.slice(0, BLOCK_LABEL_PREVIEW_MAX_LENGTH).trimEnd()}...`;
}

/**
 * Every block gets an auto-assigned or teacher-customized `name` (see block-naming.ts) — this
 * is the label to show anywhere a block needs to be identified. The position+type+question
 * fallback only applies to a block that hasn't been through backfill yet (shouldn't normally
 * happen post-load, but keeps this safe to call before that's guaranteed).
 */
export function getBlockLabel(block: Block, index: number, t: (key: string) => string): string {
  if (block.name) return block.name;
  const meta = BLOCK_META[block.type];
  const typeLabel = meta ? t(meta.labelKey) : block.type;
  const question = (block.data as { question?: string }).question;
  const preview = question ? truncatePreview(stripHtmlForLabel(question)) : '';
  return preview ? `${index + 1}. ${typeLabel} — ${preview}` : `${index + 1}. ${typeLabel}`;
}

/** The subset of a rule's selected state ids for one block that no longer exist on that block
 * at all — used to render an orphaned/removable chip for a state that's since been deleted,
 * rather than silently dropping it from view. */
/**
 * Anything holding a condition list — a navigation rule or a change. The questions below (what does
 * this name, is it stale, is it unreachable) are about the LIST, and both lists are the same list.
 */
type ConditionHolder = Pick<FlowRule, 'conditions'>;

/**
 * The feedback-state ids a rule names on one block.
 *
 * `rule.conditions` used to BE this lookup — `Record<blockId, stateId[]>` — and a dozen callers
 * indexed it directly. The flat `FlowCondition[]` is better for authoring (one meaning for
 * matchMode, and attempts can sit in the same list) but it cannot be indexed, so the lookup moves
 * here rather than into each caller.
 *
 * Feedback conditions only: an attempts condition names no block and no state, and every caller of
 * this is asking a question about feedback states — is this one stale, inactive, contradictory.
 */
export function conditionStateIds(rule: ConditionHolder, blockId: string): string[] {
  return rule.conditions
    .filter((c): c is Extract<FlowCondition, { kind: 'feedback' }> => c.kind === 'feedback' && c.blockId === blockId)
    .map((c) => c.stateId);
}

/** Every block a rule's feedback conditions name, in first-mention order. Was `Object.keys`. */
export function conditionBlockIds(rule: ConditionHolder): string[] {
  const seen: string[] = [];
  for (const c of rule.conditions) {
    if (c.kind === 'feedback' && !seen.includes(c.blockId)) seen.push(c.blockId);
  }
  return seen;
}

export function getStaleStateIds(rule: ConditionHolder, blockId: string, blocks: Block[]): string[] {
  const block = blocks.find((b) => b.id === blockId);
  return conditionStateIds(rule, blockId).filter((id) => resolveConditionId(block, id) === undefined);
}

/**
 * A rule condition is broken if its block is gone, or that block's feedback list no longer
 * contains a state with that id at all — a Special event can target either an incorrect or a
 * neutral named state, so evaluation itself is no longer part of what makes a reference stale.
 * Pure read over the already-in-memory `blocks` state — never calls evaluate(), never touches a
 * block component.
 */
export function getBrokenConditionBlockIds(rule: ConditionHolder, blocks: Block[]): string[] {
  return conditionBlockIds(rule).filter((blockId) => getStaleStateIds(rule, blockId, blocks).length > 0);
}

/** The subset of a rule's selected state ids for one block that still exist but are currently
 * unreachable (see inactiveReasonFor) — distinct from getStaleStateIds' "gone entirely": these
 * states are still there, just switched off or no longer applicable, and could become reachable
 * again if the teacher reverts the setting that disabled them. Never includes an id that's also
 * stale (a block already returns undefined for `blocks.find`, so nothing to check). */
export function getInactiveSelectedStateIds(rule: ConditionHolder, blockId: string, blocks: Block[]): string[] {
  const block = blocks.find((b) => b.id === blockId);
  if (!block) return [];
  return conditionStateIds(rule, blockId).filter((id) => {
    const state = resolveConditionId(block, id);
    return state ? !isActiveForFlow(block, state) : false;
  });
}

export function getInactiveConditionBlockIds(rule: ConditionHolder, blocks: Block[]): string[] {
  return conditionBlockIds(rule).filter((blockId) => getInactiveSelectedStateIds(rule, blockId, blocks).length > 0);
}

/**
 * The single source of truth for "what does this condition currently mean" — one of:
 *   - `'deleted'` — the block itself is gone, or the state id no longer exists on it.
 *   - `'inactive:<reasonKey>'` — the state still exists but can't currently fire (see
 *     inactiveReasonFor).
 *   - `'active:<evaluation>'` — the state exists and is reachable, with this evaluation.
 *   - `'active:<evaluation>:<branchScopeKey>'` — same, but for a branch-collapsed condition, with
 *     an extra segment so a change in WHICH branches contribute this condition also counts as
 *     drift, not just a change in evaluation. Keyed on the stable, order-independent branch-id set
 *     (a `'*'` sentinel for "every currently-authored branch") rather than ordinal position —
 *     branches can only be appended/removed, never reordered, so keying on ordinals would
 *     spuriously flag every rule referencing later branches whenever an earlier, unrelated branch
 *     is deleted and everything after it renumbers.
 * Every "has this condition changed since setup" check in this file, and the persisted
 * `conditionAcknowledged` snapshot itself (see FlowRule's doc comment in @agavi/blocks), compares
 * against exactly this signature — so a condition being deleted, deactivated, reactivated,
 * reclassified, or having its branch-scope change are all just "the signature changed" to this one
 * piece of code, rather than five separately-tracked failure modes.
 */
export function getConditionSignature(blocks: Block[], blockId: string, id: string): string {
  const block = blocks.find((b) => b.id === blockId);
  if (!block) return 'deleted';
  const state = resolveConditionId(block, id);
  if (!state) return 'deleted';
  const reason = inactiveReasonFor(block, state);
  if (reason) return `inactive:${reason}`;
  if (state.scope) {
    const { parts, versionTotal, cells } = state.scope;
    /* `*` for a scope that covers everything, exactly as the branch-only path does: "any version,
       any branch" stays stable when an unrelated version or branch is added, because the
       condition genuinely did not change. Anything narrower keys on the CELL IDS — ordinals
       renumber when a neighbour is deleted, and a renumber is not a change. */
    const everything = parts.length === versionTotal
      && parts.every((p) => p.branchTotal === 0 || p.branchOrdinals.length === p.branchTotal);
    const scopeKey = everything
      ? '*'
      : cells.map((c) => `${c.versionId ?? ''}/${c.branchId ?? ''}`).sort().join('+');
    return `active:${state.evaluation}:${scopeKey}`;
  }
  const scope = state.branchScope;
  if (!scope || scope.total <= 1) return `active:${state.evaluation}`;
  const scopeKey = scope.ordinals.length === scope.total ? '*' : [...scope.branchIds].sort().join('+');
  return `active:${state.evaluation}:${scopeKey}`;
}

/** Turns a status signature into a short display phrase for the before/after diff — "Correct",
 * "Inactive (math error checking is off)", "Deleted" — so every cause reads as the same kind of
 * one-word-ish adjective in "was {X}, is now {Y}", regardless of which of the three signature
 * shapes it came from. Tolerates getConditionSignature's optional `:<branchScopeKey>` suffix on an
 * `active:` signature — the evaluation is always the first captured group either way. */
function signatureLabel(signature: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (signature === 'deleted') return t('task.flow.rule.signature.deleted');
  // The signature still carries WHY (`inactive:mathErrorUnreachable`) because the diff compares
  // signatures; the label deliberately drops it. Nine reasons rendered as a parenthetical made the
  // "was X, is now Y" line a paragraph, and the issues panel already states the cause.
  if (/^inactive:/.test(signature)) return t('task.flow.rule.signature.unreachable');
  const activeMatch = /^active:(correct|incorrect|neutral)(?::.+)?$/.exec(signature);
  if (activeMatch) {
    const evaluation = activeMatch[1];
    return evaluation === 'correct' ? t('blocks.feedbackCard.evalCorrect') : evaluation === 'incorrect' ? t('blocks.feedbackCard.evalIncorrect') : t('blocks.feedbackCard.evalNeutral');
  }
  return signature;
}

/**
 * Refreshes a rule's diagnostic label snapshots to match the CURRENT live title of every
 * condition that's still live — called whenever a rule's conditions change, so the snapshot
 * always reflects the most recently known-good label rather than whatever it happened to be
 * captured as originally. Never touches a snapshot entry whose block/state is already gone —
 * there's nothing live to refresh from, and the whole point of keeping it around is to preserve
 * what it USED to say for exactly that situation.
 *
 * Also freezes `conditionAcknowledged` for any condition that doesn't already have one — i.e. the
 * moment a state is first selected, not on every subsequent call. Unlike labels, an existing
 * signature is never overwritten here: that would silently clear a pause the instant a teacher
 * touched anything else on the rule (title, match mode, an unrelated condition), before anyone
 * had a chance to see it. Only acknowledgeRule re-freezes an existing signature.
 */
export function refreshConditionLabels(rule: FlowRule, blocks: Block[], t: (key: string, opts?: Record<string, unknown>) => string, locale?: string): FlowRule {
  const conditionLabels: Record<string, Record<string, string>> = { ...rule.conditionLabels };
  const conditionBlockLabels: Record<string, string> = { ...rule.conditionBlockLabels };
  const conditionAcknowledged: Record<string, Record<string, string>> = { ...rule.conditionAcknowledged };
  for (const blockId of conditionBlockIds(rule)) {
    const ids = conditionStateIds(rule, blockId);
    const idx = blocks.findIndex((b) => b.id === blockId);
    const block = blocks[idx];
    if (!block) continue;
    conditionBlockLabels[blockId] = getBlockLabel(block, idx, t);
    const labels = { ...conditionLabels[blockId] };
    const acknowledged = { ...conditionAcknowledged[blockId] };
    for (const id of ids) {
      const state = resolveConditionId(block, id);
      if (state) labels[id] = flowStateLabel(state, t, block, locale);
      if (acknowledged[id] === undefined) acknowledged[id] = getConditionSignature(blocks, blockId, id);
    }
    conditionLabels[blockId] = labels;
    conditionAcknowledged[blockId] = acknowledged;
  }
  return { ...rule, conditionLabels, conditionBlockLabels, conditionAcknowledged };
}

function isDrifted(rule: FlowRule, blocks: Block[]): boolean {
  return rule.conditions.some((c) => {
    if (c.kind !== 'feedback') return false;
    const acknowledged = rule.conditionAcknowledged?.[c.blockId]?.[c.stateId];
    return acknowledged !== undefined && acknowledged !== getConditionSignature(blocks, c.blockId, c.stateId);
  });
}

/**
 * Block ids where this rule's own selected conditions can NEVER all be true together — the one
 * contradiction Flow can detect with total certainty, independent of any block-type-specific
 * knowledge: the generic 'incorrect' (Default Incorrect) trigger is architecturally guaranteed
 * mutually exclusive with every other named state on the same block (evaluate() either returns
 * the specific matched states, or falls back to Default Incorrect alone — never both; verified
 * across every block type sharing that convention). Selecting Default Incorrect alongside
 * another state for one block only matters under matchMode 'all' — under 'any', either one alone
 * already satisfies the block, so there's nothing to contradict.
 *
 * Deliberately doesn't try to detect contradictions between two NON-incorrect states (two
 * math-error categories, two value-feedback ranges, two MCQ option-feedbacks) — whether a
 * specific pair of those can co-occur depends on the actual values a teacher configured, which
 * only that block type's own logic could determine, and even there it's advisory-only (a warning
 * label, not a guarantee — see numerical.ts's findOverlappingFeedbackPairs). Flow trying to
 * replicate that reasoning would mean duplicating fragile, block-type-specific logic; this stays
 * limited to the one relationship that's true by construction, everywhere.
 *
 * KNOWN, DEFERRED GAP (not built): branch-collapsed conditions introduce a second category of
 * guaranteed contradiction this doesn't check — two targeted (or default) conditions on the same
 * block whose branch-scopes (FeedbackStateShape.branchScope) are disjoint can also never co-fire
 * together under matchMode 'all', since only one branch resolves per submission. E.g. a condition
 * only reachable in branches 2–3 and another only reachable in branches 5–6 can't both be true for
 * one answer, the same way Default Incorrect can't co-occur with anything else — just for a
 * branch-scope reason instead of an evaluation-category reason. Left unbuilt: reasoning correctly
 * about partial branch-scope overlap (some shared, some not) across an arbitrary number of
 * conditions is a genuinely harder problem than this function's current one relationship, and was
 * explicitly scoped out when branch-awareness was added here — a future pass, not an oversight.
 */
export function getContradictoryBlockIds(rule: FlowRule, blocks: Block[]): string[] {
  if (rule.matchMode !== 'all') return [];
  return conditionBlockIds(rule).filter((blockId) => {
    const ids = conditionStateIds(rule, blockId);
    if (ids.length < 2) return false;
    const block = blocks.find((b) => b.id === blockId);
    const hasIncorrect = ids.some((id) => resolveConditionId(block, id)?.trigger === 'incorrect');
    const hasOther = ids.some((id) => resolveConditionId(block, id)?.trigger !== 'incorrect');
    return hasIncorrect && hasOther;
  });
}

/** A rule is paused when either: (a) any of its conditions' current signature (see
 * getConditionSignature) no longer matches what was last acknowledged for it — covering every way
 * a condition's meaning can change since setup (deleted, deactivated, reactivated, reclassified);
 * or (b) it contains a guaranteed contradiction (see getContradictoryBlockIds) — wrong from the
 * moment it was configured, not something that changed later. A paused-for-drift rule isn't
 * necessarily broken — it may still be fully configured and would still work, just unconfirmed —
 * so resolveFlow refuses to act on the evaluation-drift half of this (see @agavi/blocks'
 * resolveFlow) until it's acknowledged. A paused-for-contradiction rule genuinely can never fire
 * at all; resolveFlow needs no special handling for that case (the AND it can't satisfy simply
 * never becomes true), but the UI surfaces it so a teacher isn't left wondering why a Special
 * event never seems to trigger. */
export function isRulePaused(rule: FlowRule, blocks: Block[]): boolean {
  return isDrifted(rule, blocks) || getContradictoryBlockIds(rule, blocks).length > 0;
}

/**
 * Everything wrong with one event, as ONE thing with a severity.
 *
 * There were two mechanisms: `paused` (drift or contradiction, amber) and a separate `problem`
 * string (no conditions, red). Two colours and two presentations for one question a teacher asks
 * once — "will this fire, and if not why" — and the split meant a card could be red for one reason
 * while silently also being wrong for another.
 *
 * Severity is what the two kinds actually differ by, and it is not arbitrary:
 *   - ERROR is "can never fire". No conditions at all, or a contradiction, which is wrong from the
 *     moment it was configured rather than something that changed later.
 *   - WARNING is "would fire, but its meaning moved". Drift: still configured, still workable, and
 *     `resolveFlow` deliberately declines to act on it until a teacher confirms.
 *
 * `conditionKeys` names WHICH conditions are implicated, so an expanded card can mark the rows at
 * fault instead of tinting the whole thing and leaving the teacher to find them.
 */
export type RuleProblemSeverity = 'error' | 'warning';

export interface RuleProblem {
  severity: RuleProblemSeverity;
  /** Matches the keys `FlowConditionList` builds for its rows. */
  conditionKeys: Set<string>;
}

/**
 * The key a condition row is identified by, shared so the list and this cannot drift apart.
 *
 * The INDEX is in every key, including a feedback one. It reads as redundant — a block and a state
 * ought to identify a feedback condition uniquely — and it was, until a duplicate could be added:
 * two identical conditions then produced one key, React mismatched the rows, and removing either
 * took the wrong one. Adding duplicates is prevented at the picker now, but a config saved before
 * that guard still holds them, and a list that cannot render its own stored data is a worse failure
 * than the one it came from.
 */
export function conditionKey(c: FlowCondition, index: number): string {
  // A counting condition names no block and no state, so the list position is all there is to
  // key it by. The kind is in there to READ: `at:1` on a card holding both an attempts and a
  // visits row says nothing about which row is being pointed at.
  return c.kind === 'feedback' ? `fb:${index}:${c.blockId}:${c.stateId}` : `${c.kind}:${index}`;
}

export function getRuleProblem(rule: FlowRule, blocks: Block[]): RuleProblem | null {
  if (rule.conditions.length === 0) return { severity: 'error', conditionKeys: new Set() };

  const contradictory = new Set(getContradictoryBlockIds(rule, blocks));
  if (contradictory.size > 0) {
    // Every condition on a contradicting block is implicated: the pair is the fault, so marking one
    // of them would be picking a culprit out of two that are only wrong together.
    const keys = new Set(rule.conditions
      .map((c, i) => (c.kind === 'feedback' && contradictory.has(c.blockId) ? conditionKey(c, i) : null))
      .filter((k): k is string => k !== null));
    return { severity: 'error', conditionKeys: keys };
  }

  const drifted = new Set(rule.conditions
    .map((c, i) => {
      if (c.kind !== 'feedback') return null;
      const acknowledged = rule.conditionAcknowledged?.[c.blockId]?.[c.stateId];
      if (acknowledged === undefined) return null;
      return getConditionSignature(blocks, c.blockId, c.stateId) === acknowledged ? null : conditionKey(c, i);
    })
    .filter((k): k is string => k !== null));
  if (drifted.size > 0) return { severity: 'warning', conditionKeys: drifted };

  // Drift can also sit on a condition that is no longer in the list at all — a stale or inactive
  // reference, which renders in its own section. `isDrifted` sees those; the loop above cannot.
  return isDrifted(rule, blocks) ? { severity: 'warning', conditionKeys: new Set() } : null;
}

/**
 * ═══ ONE STATE: A CARD RUNS, OR IT IS PAUSED ═══════════════════════════════════════════════════
 *
 * There were three words for one fact. A rule with no conditions said "Ignored" in red; a drifted
 * one said "Paused" in amber and offered Accept; a contradictory one was quietly both. Three
 * vocabularies, two colours, and the teacher's next move identical in every case: go and fix it.
 *
 * The split also claimed a difference that is not there. ALL THREE already fail to fire —
 * `conditionsSatisfied` guards an empty list before the any/all split, a contradiction can never
 * hold, and `resolveFlow` declines a drifted rule on purpose. "Paused" is simply the honest name
 * for what already happens, so naming them alike costs no behaviour and removes two words.
 *
 * `kind` survives severity because the REMEDY differs even though the state does not: only drift
 * can be accepted as-is, the rest have to be edited. That is one button, not a second colour.
 *
 * DUPLICATE is deliberately NOT here. Two identical events both fire today — the first wins by
 * ladder order and the second is unreachable — so pausing them would take away a route the lesson
 * currently takes. It is redundancy, not malfunction: flagged (see `duplicateCardIds`), never
 * paused. That is also why this returns at most one problem: they are all the same state.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export type CardProblemKind =
  /** Nothing to match on yet. Only ever reported once the card has been LEFT — see `settled`. */
  | 'incomplete'
  /** Configured, but can never hold: conditions contradict, or one points at something deleted. */
  | 'broken'
  /** Configured and could fire, but a condition's meaning moved since it was confirmed. */
  | 'drifted'
  /** The task this routes to has been deleted. Named separately from `broken` because the card
   *  can say WHICH destination went away, from the title snapshotted on the action. */
  | 'targetDeleted'
  /** A Change with conditions but nothing to change -- the mirror of having no conditions. */
  | 'noEffects'
  /**
   * An earlier event in the ladder already catches exactly these conditions, so this one can
   * never be reached. NAVIGATION ONLY, and the asymmetry is the resolution model rather than a
   * judgement call: navigation is first-match-wins, so a second event on the same conditions is
   * dead whatever its action; Changes apply ALL matches, so two of them on the same conditions
   * both run and are perfectly ordinary.
   */
  | 'unreachable';

export interface CardProblem {
  kind: CardProblemKind;
  /** Matches the keys `FlowConditionList` builds for its rows, so an open card can mark them. */
  conditionKeys: Set<string>;
  /** Only drift can be blessed as-is; everything else has to be edited. */
  acceptable: boolean;
}

/** The shape both card types satisfy. A change carries no `conditionAcknowledged`, so it can be
 *  incomplete or broken but never drifted — which is correct: nothing was ever acknowledged. */
export interface PausableCard {
  matchMode: 'any' | 'all';
  conditions: FlowCondition[];
  conditionAcknowledged?: Record<string, Record<string, string>>;
}

/**
 * What a card DOES, checked alongside what triggers it.
 *
 * The predicate examined conditions only, which left three ways for a card to look configured
 * and do nothing: a route whose destination was deleted, a Change with no effects at all, and a
 * Change whose effect names a block that is gone. A dead outcome is as inert as a dead trigger,
 * and the pause model exists to stop exactly that being invisible.
 */
export interface CardOutcome {
  /** Navigation: the action, resolved against the tasks that still exist. */
  action?: FlowAction;
  /** Changes: the effects, resolved against the blocks that still exist. */
  effects?: { blockId: string }[];
}

/**
 * Outcome validation ALONE, for a card that has no conditions of its own.
 *
 * The built-in events and the sensor rungs are fixed rungs of the ladder: what triggers them is
 * structural, so `incomplete` and `drifted` cannot apply and `getCardProblem` would wrongly
 * report an empty condition list as unfinished. What they CAN do is route somewhere that has been
 * deleted -- which is not a sensor or built-in concern at all, just the same dead target any
 * action can hold. Only the outcome half of the model fits them, and this is that half.
 */
export function getOutcomeProblem(
  outcome: CardOutcome, blocks: Block[], tasks?: { id: string }[],
): CardProblem | null {
  const kind = outcomeProblem(outcome, blocks, tasks);
  return kind ? { kind, conditionKeys: new Set(), acceptable: false } : null;
}

function outcomeProblem(outcome: CardOutcome | undefined, blocks: Block[], tasks: { id: string }[] | undefined): CardProblemKind | null {
  if (!outcome) return null;

  const action = outcome.action;
  if (tasks && action?.type === 'goto' && action.targetTaskId !== null
      && !tasks.some((tk) => tk.id === action.targetTaskId)) {
    return 'targetDeleted';
  }

  /* `Array.isArray`, not truthiness: a Change stored before `effects` was a list holds a non-array
     here, and `.some` on it took the experience board down. An unreadable shape is no effects. */
  if (outcome.effects) {
    if (!Array.isArray(outcome.effects) || outcome.effects.length === 0) return 'noEffects';
    if (outcome.effects.some((e) => !blocks.some((b) => b.id === e.blockId))) return 'broken';
  }
  return null;
}

/**
 * HOW A FLOW CARD IS ADDRESSED FROM OUTSIDE THE PANEL.
 *
 * The board's problems panel reports a rule or a Change that cannot run, and a teacher clicking
 * that row expects to land on the card itself — which means the id has to survive a URL and come
 * back as a DOM query. One function for it, because three places would otherwise each spell the
 * same string: the row, the `data-flow-card` the cards carry, and the lookup that scrolls to one.
 */
export function flowCardKey(kind: 'rule' | 'change', id: string): string {
  return `${kind}:${id}`;
}

/**
 * Whether this card runs, and if not, why.
 *
 * `settled` is what stops a brand new card scolding its author. A card is incomplete the instant
 * it is created — that is not information, it is the definition of new. Leaving it incomplete IS
 * information, so the check applies only once the card has been collapsed or focus has moved out
 * of it. Every other kind reports immediately: those describe work already done wrongly, not work
 * not yet done.
 */
export function getCardProblem(
  card: PausableCard, blocks: Block[], settled: boolean,
  outcome?: CardOutcome, tasks?: { id: string }[],
): CardProblem | null {
  /*
   * EVERY SHAPE EVER PERSISTED, because the board reads stored flow RAW.
   *
   * `conditions` was a `Record<blockId, stateId[]>` before it was a list, and
   * `normalizeFlowConfig` migrates it — on the task editor's load path only. The experience
   * board walks every task's flow without normalizing, so a card still holding the old shape
   * reached `.length` on a record and took the whole page down with it. Seven of them were
   * sitting in the dev database across five experiences; the owner met one as "why is this
   * experience not loading?".
   *
   * Through `migrateConditionShape`, not a truthiness guard: an old record can hold REAL
   * conditions, and reading it as "none" would report a finished card as incomplete and
   * quietly drop what it matches on.
   *
   * `feedback_structural_patch_deletes_by_omission` is why this is urgent rather than
   * cosmetic: a board that throws renders fewer tasks than it has, and the next structural
   * autosave deletes the difference.
   */
  const conditions = migrateConditionShape(card.conditions);
  const safe = (conditions === card.conditions ? card : { ...card, conditions }) as PausableCard;

  /*
   * A dead OUTCOME is reported before an unfinished trigger, and immediately rather than on
   * settle: "the task you route to was deleted" is something that happened TO this card, not
   * work the teacher has yet to do, so waiting to mention it would be waiting for them to
   * discover it themselves.
   */
  const dead = outcomeProblem(outcome, blocks, tasks);
  if (dead === 'targetDeleted' || dead === 'broken') {
    return { kind: dead, conditionKeys: new Set(), acceptable: false };
  }

  if (conditions.length === 0) {
    return settled ? { kind: 'incomplete', conditionKeys: new Set(), acceptable: false } : null;
  }

  // Nothing to change is the mirror of nothing to match on, so it waits for settle in the same way.
  if (dead === 'noEffects') {
    return settled ? { kind: 'noEffects', conditionKeys: new Set(), acceptable: false } : null;
  }

  // A condition naming a block or state that is gone can never hold, and unlike drift there is
  // nothing to accept — the thing it referred to does not exist to be re-confirmed.
  const stale = new Set(conditions
    .map((c, i) => (c.kind === 'feedback' && getStaleStateIds(safe, c.blockId, blocks).includes(c.stateId)
      ? conditionKey(c, i) : null))
    .filter((k): k is string => k !== null));
  if (stale.size > 0) return { kind: 'broken', conditionKeys: stale, acceptable: false };

  const contradictory = new Set(getContradictoryBlockIds(safe as FlowRule, blocks));
  if (contradictory.size > 0) {
    // Every condition on a contradicting block is implicated: the pair is the fault, so marking
    // one of them would be picking a culprit out of two that are only wrong together.
    const keys = new Set(conditions
      .map((c, i) => (c.kind === 'feedback' && contradictory.has(c.blockId) ? conditionKey(c, i) : null))
      .filter((k): k is string => k !== null));
    return { kind: 'broken', conditionKeys: keys, acceptable: false };
  }

  const drifted = new Set(conditions
    .map((c, i) => {
      if (c.kind !== 'feedback') return null;
      const acknowledged = card.conditionAcknowledged?.[c.blockId]?.[c.stateId];
      if (acknowledged === undefined) return null;
      return getConditionSignature(blocks, c.blockId, c.stateId) === acknowledged ? null : conditionKey(c, i);
    })
    .filter((k): k is string => k !== null));
  if (drifted.size > 0) return { kind: 'drifted', conditionKeys: drifted, acceptable: true };

  // Drift can also sit on a condition no longer in the list at all — a stale or inactive
  // reference, which renders in its own section. `isDrifted` sees those; the loop above cannot.
  return isDrifted(safe as FlowRule, blocks)
    ? { kind: 'drifted', conditionKeys: new Set(), acceptable: true }
    : null;
}

/**
 * The ids of cards that say exactly the same thing as another card in the same list.
 *
 * Identical means BOTH halves: the same conditions under the same match mode, AND the same
 * outcome. Effects alone would be wrong for Changes — two changes opening one setting under
 * different conditions is the whole reason a card may repeat another's effect, since one card
 * carries one match mode and `(A and B) or (C and D)` needs two.
 *
 * Every member of a group is returned, not just the later ones: neither card is the culprit, they
 * are only redundant together, and marking one would be arbitrary.
 */
/**
 * Events that an earlier event in the list has already caught.
 *
 * Conditions ALONE, deliberately — the action is not part of it. Two events on "attempt 3", one
 * going to another task and one staying, is not redundancy but a silent contradiction: the ladder
 * takes the first and the teacher's second intention never happens. Matching on conditions AND
 * action missed exactly that case, which is the one worth catching.
 *
 * Only the LATER card is named, unlike `duplicateCardIds`. Here there IS a culprit: order decides
 * which one runs, so the earlier is simply the rule and the later is the one that cannot fire.
 */
export function shadowedCardIds(rules: { id: string; matchMode: 'any' | 'all'; conditions: FlowCondition[] }[]): Map<string, string> {
  const seen = new Map<string, string>();
  const shadowed = new Map<string, string>();
  for (const rule of rules) {
    if (rule.conditions.length === 0) continue; // Already reported as incomplete; not a shadow.
    const fingerprint = `${rule.matchMode}#${rule.conditions.map(conditionFingerprint).sort().join('|')}`;
    const first = seen.get(fingerprint);
    if (first === undefined) seen.set(fingerprint, rule.id);
    else shadowed.set(rule.id, first);
  }
  return shadowed;
}

/** A condition by its MEANING, with no list position in it — for comparing two cards. */
function conditionFingerprint(c: FlowCondition): string {
  if (c.kind === 'feedback') return `fb:${c.blockId}:${c.stateId}`;
  // The snapshot title is deliberately NOT in here: two conditions naming the same task are the
  // same condition however stale one of their labels has gone.
  if (c.kind === 'visits') return `vi:${c.taskId ?? 'here'}:${compareFingerprint(c.compare)}`;
  const subject = c.subject.scope === 'block' ? `block:${c.subject.blockId}` : 'task';
  return `at:${subject}:${c.filter}:${compareFingerprint(c.compare)}`;
}

function compareFingerprint(c: AttemptCompare): string {
  return c.op === 'between' ? `between:${c.lo}:${c.hi}` : `${c.op}:${c.n}`;
}

export function duplicateCardIds(cards: { id: string; matchMode: 'any' | 'all'; conditions: FlowCondition[] }[],
  outcomeOf: (card: { id: string }) => string): Set<string> {
  const byFingerprint = new Map<string, string[]>();
  for (const card of cards) {
    // Conditions SORTED, so the same pair listed either way is the same rule — and fingerprinted
    // by content rather than through `conditionKey`, which carries the list index and reduces an
    // attempts condition to that index alone. Two attempts conditions differ by their subject,
    // filter and comparison, all of which have to be in here or every one looks alike.
    const conditions = card.conditions.map(conditionFingerprint).sort().join('|');
    const fingerprint = `${card.matchMode}#${conditions}#${outcomeOf(card)}`;
    byFingerprint.set(fingerprint, [...(byFingerprint.get(fingerprint) ?? []), card.id]);
  }
  const dupes = new Set<string>();
  for (const ids of byFingerprint.values()) if (ids.length > 1) ids.forEach((id) => dupes.add(id));
  return dupes;
}

export interface ConditionChange {
  blockLabel: string;
  stateLabel: string;
  after: string;
}

/** What every changed condition on this rule (deleted, deactivated, reactivated, or reclassified
 * — see getConditionSignature) looks like NOW, for the "this event changed since you set it up"
 * banner. Deliberately doesn't restate what it used to be — the teacher already knows what they
 * set up; what they need is what it turned into. Prefers the label snapshot
 * (conditionLabels/conditionBlockLabels) once a block/state is gone, since there's no live title
 * left to read; uses the live title otherwise. */
export function getRuleChanges(rule: FlowRule, blocks: Block[], t: (key: string, opts?: Record<string, unknown>) => string, locale?: string): ConditionChange[] {
  const changes: ConditionChange[] = [];
  for (const blockId of conditionBlockIds(rule)) {
    const block = blocks.find((b) => b.id === blockId);
    const idx = blocks.findIndex((b) => b.id === blockId);
    const blockLabel = block ? getBlockLabel(block, idx, t) : (rule.conditionBlockLabels?.[blockId] ?? t('task.flow.rule.unknownBlock'));
    for (const id of conditionStateIds(rule, blockId)) {
      const acknowledged = rule.conditionAcknowledged?.[blockId]?.[id];
      if (acknowledged === undefined) continue;
      const current = getConditionSignature(blocks, blockId, id);
      if (current === acknowledged) continue;
      const state = resolveConditionId(block, id);
      const stateLabel = state ? flowStateLabel(state, t, block, locale) : (rule.conditionLabels?.[blockId]?.[id] ?? t('task.flow.rule.unknownFeedback'));
      changes.push({ blockLabel, stateLabel, after: signatureLabel(current, t) });
    }
  }
  return changes;
}

export interface ConditionContradiction {
  blockLabel: string;
  stateLabels: string[];
}

/** The full detail for every contradictory block on this rule (see getContradictoryBlockIds),
 * for the same "this event changed since you set it up" banner — a contradiction isn't really a
 * change over time, but it shares the same "this event needs your attention before it can work"
 * surface, so it renders alongside getRuleChanges rather than in a separate system. */
export function getRuleContradictions(rule: FlowRule, blocks: Block[], t: (key: string, opts?: Record<string, unknown>) => string, locale?: string): ConditionContradiction[] {
  return getContradictoryBlockIds(rule, blocks).map((blockId) => {
    const idx = blocks.findIndex((b) => b.id === blockId);
    const block = blocks[idx]!;
    const blockLabel = getBlockLabel(block, idx, t);
    const stateLabels = conditionStateIds(rule, blockId)
      .map((id) => resolveConditionId(block, id))
      .filter((s): s is FeedbackStateShape => !!s)
      .map((s) => flowStateLabel(s, t, block, locale));
    return { blockLabel, stateLabels };
  });
}

/**
 * Called when a teacher acknowledges a rule's paused banner: re-freezes `conditionAcknowledged`
 * to the CURRENT signature of every selected condition — including one whose block or state is
 * gone entirely (getConditionSignature returns 'deleted' regardless of whether the block still
 * exists), so a teacher can dismiss a warning about a deleted reference without being forced to
 * remove that condition from the rule first. Silences isRulePaused until something changes again.
 * Distinct from refreshConditionLabels, which runs opportunistically on every edit — this only
 * ever runs on an explicit teacher action, since silently re-acknowledging on every render would
 * defeat the entire point (the pause would vanish before anyone saw it).
 */
export function acknowledgeRule(rule: FlowRule, blocks: Block[]): FlowRule {
  const conditionAcknowledged: Record<string, Record<string, string>> = { ...rule.conditionAcknowledged };
  for (const blockId of conditionBlockIds(rule)) {
    const acknowledged = { ...conditionAcknowledged[blockId] };
    for (const id of conditionStateIds(rule, blockId)) acknowledged[id] = getConditionSignature(blocks, blockId, id);
    conditionAcknowledged[blockId] = acknowledged;
  }
  return { ...rule, conditionAcknowledged };
}

export function isGotoTargetValid(action: FlowAction, tasks: Task[]): boolean {
  // A null target is an intentional loose thread, not a broken reference — treat as valid.
  return action.type !== 'goto' || action.targetTaskId === null || tasks.some((t) => t.id === action.targetTaskId);
}

/** Default title for a newly added Special event — unlike block names, event titles aren't
 * uniqueness-enforced, so this is just a reasonable starting point the teacher can rename. */
export function nextRuleTitle(rules: FlowRule[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t('task.flow.special.defaultTitle', { index: rules.length + 1 });
}

/** The same, for a change — "Change Event 2". A name it can be found by before it does anything,
 *  which is the state a change spends its first minute in. */
export function nextChangeTitle(changes: { id: string }[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  return t('task.flow.changes.defaultTitle', { index: changes.length + 1 });
}

/**
 * Rewrites a rule's stored condition ids to their current canonical form — a no-op for every
 * condition whose id already resolves directly (every non-branching block, and the overwhelming
 * majority of the time generally). The one case this actually changes anything: a rule was
 * authored against a block's top-level state id BEFORE that block had `conditionalBranches` (or
 * while branching was briefly off), and the block has since turned branching on — feedbackStatesOf
 * now returns a synthetic group id for that same condition instead. resolveConditionId already
 * handles this defensively at read time (so nothing here is strictly required for correctness),
 * but self-healing the STORED id here means every other piece of code in this file (and
 * @agavi/blocks' resolveFlow, which has no branch-aware fallback of its own) keeps working with a
 * plain, direct id match going forward, rather than relying on the fallback search every time.
 * Carries the old id's label/acknowledged-signature snapshots forward under the new id so neither
 * silently vanishes — note the carried-forward acknowledged signature is the OLD, unscoped one
 * (`active:incorrect`), which will legitimately disagree with the freshly-computed scoped one
 * (`active:incorrect:*`) the moment this runs — that's correct: the rule should read as "please
 * review, branch scope changed" the first load after a teacher turns branching on for its block,
 * not have that fact silently absorbed. */
function migrateConditionIds(rule: FlowRule, blocks: Block[]): FlowRule {
  const conditionLabels: Record<string, Record<string, string>> = { ...rule.conditionLabels };
  const conditionAcknowledged: Record<string, Record<string, string>> = { ...rule.conditionAcknowledged };
  const conditions = rule.conditions.map((c) => {
    // Attempts conditions name no state, so there is no id to canonicalize.
    if (c.kind !== 'feedback') return c;
    const block = blocks.find((b) => b.id === c.blockId);
    const canonical = resolveConditionId(block, c.stateId)?.id;
    if (!canonical || canonical === c.stateId) return c;
    const oldLabel = rule.conditionLabels?.[c.blockId]?.[c.stateId];
    if (oldLabel !== undefined) conditionLabels[c.blockId] = { ...conditionLabels[c.blockId], [canonical]: oldLabel };
    const oldAcknowledged = rule.conditionAcknowledged?.[c.blockId]?.[c.stateId];
    if (oldAcknowledged !== undefined) conditionAcknowledged[c.blockId] = { ...conditionAcknowledged[c.blockId], [canonical]: oldAcknowledged };
    return { ...c, stateId: canonical };
  });
  return { ...rule, conditions, conditionLabels, conditionAcknowledged };
}

/**
 * Whatever was stored where a `FlowCondition[]` now goes, read as one.
 *
 * Every rule authored before the list existed holds the nested `Record<blockId, stateId[]>` the
 * grid wrote. Read as a flat list of feedback conditions it means exactly the same thing: matchMode
 * 'all' over (block, state) pairs is what the nested form computed anyway, and 'any' likewise — the
 * two levels the old shape had were always collapsed by the same setting. No teacher-visible
 * behaviour changes across the migration, which is the only reason it can run silently on load.
 *
 * Deliberately total: anything unrecognisable reads as no conditions rather than throwing. A rule
 * with no conditions is inert and visible as such in the panel; a parse error on load would take
 * the whole task's flow down with it.
 */
export function migrateConditionShape(raw: unknown): FlowCondition[] {
  if (Array.isArray(raw)) {
    return raw.filter((c): c is FlowCondition => {
      if (!c || typeof c !== 'object') return false;
      const kind = (c as { kind?: unknown }).kind;
      return kind === 'feedback' || kind === 'attempts' || kind === 'visits';
    });
  }
  if (!raw || typeof raw !== 'object') return [];
  const out: FlowCondition[] = [];
  for (const [blockId, ids] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(ids)) continue;
    for (const stateId of ids) {
      if (typeof stateId === 'string') out.push({ kind: 'feedback', blockId, stateId });
    }
  }
  return out;
}

/**
 * Whether a stored effect is one this build understands.
 *
 * The one place a new `FlowEffect` kind has to be taught about, and the one place forgetting is
 * invisible: an unrecognised effect is dropped on load, silently, and the teacher sees their
 * change quietly lose a row rather than an error. Widened here at the same time as the union or
 * the feature does not survive a reload — which is exactly how `FlowChange.title` was lost.
 *
 * Deliberately shallow. It checks that the shape is one of the three and that its own fields are
 * the right types; it does not check that the block still exists or still has the setting, because
 * a block can be edited between two loads and an effect naming a setting that is currently off is
 * a thing the card is built to show, not a thing to delete behind the teacher.
 */
function isStoredEffect(e: unknown): e is FlowEffect {
  if (!e || typeof e !== 'object') return false;
  const eff = e as Partial<FlowEffect> & { blockId?: unknown };
  if (typeof eff.blockId !== 'string' || !eff.blockId) return false;
  if (eff.kind === 'unlockBypass') return true;
  if (eff.kind === 'setFlag') {
    const f = e as { field?: unknown; value?: unknown };
    return typeof f.field === 'string' && typeof f.value === 'boolean';
  }
  if (eff.kind === 'setVisibility') {
    const v = e as { mode?: unknown };
    return v.mode === 'live' || v.mode === 'capture' || v.mode === 'passive';
  }
  return false;
}

/** Same total-read treatment for a Changes entry, which has no legacy shape to migrate from —
 * only malformed/partial stored data to survive. */
function normalizeChange(
  change: FlowChange, index: number, refreshVisits: (c: FlowCondition[]) => FlowCondition[],
): FlowChange {
  return {
    id: typeof change.id === 'string' && change.id ? change.id : `change-${index + 1}`,
    // Reconstructed explicitly like every other field, which is exactly how it came to be dropped:
    // `title` was added to `FlowChange` after this function was written, and a total read that is
    // not updated does not fail loudly — it silently returns the shape it was told about. The
    // teacher's own name for a change survived until the next load and then was not there.
    ...(typeof change.title === 'string' && change.title.trim() ? { title: change.title } : {}),
    matchMode: change.matchMode === 'any' || change.matchMode === 'all' ? change.matchMode : 'all',
    conditions: refreshVisits(migrateConditionShape(change.conditions)),
    effects: Array.isArray(change.effects) ? change.effects.filter(isStoredEffect) : [],
  };
}

/**
 * One row per sensor block, stored order preserved.
 *
 * Reordering is the point of storing these at all — a teacher who wants the compass answered before
 * the GPS says so by dragging, and only a stored list can remember that. So: keep the rows for
 * blocks that still report sensor problems, in the order they're in; append a `stay` row for any
 * sensor block that doesn't have one yet; drop rows whose block is gone or no longer sensing.
 *
 * A missing row isn't a crash — resolveFlow stays put for a failing block it has no row for — so
 * this backfill is about the teacher seeing the question, not about keeping the engine safe.
 */
function normalizeSensorRules(stored: SensorRule[] | undefined, sensing: Block[]): SensorRule[] {
  const kept = (stored ?? []).filter((r) => sensing.some((b) => b.id === r.blockId));
  const deduped = kept.filter((r, i) => kept.findIndex((o) => o.blockId === r.blockId) === i);
  const missing = sensing.filter((b) => !deduped.some((r) => r.blockId === b.id));
  return [...deduped, ...missing.map((b): SensorRule => ({ blockId: b.id, action: { type: 'stay' } }))];
}

/**
 * A rung, repaired against the blocks that actually exist.
 *
 * `rules` is kept in step with the task's sensor blocks whether or not the rung is split, so
 * turning split on shows a complete list rather than one that has quietly fallen behind the blocks.
 */
function normalizeSensorRung(
  stored: SensorRung | undefined, sensing: Block[], settle: (a: FlowAction) => FlowAction,
): SensorRung {
  // Rung actions route like any other, but used to pass through raw -- no End/loose coercion and
  // no target-title refresh -- which made them the one routing surface outside the pipeline.
  return {
    action: settle(stored?.action ?? { type: 'stay' }),
    split: stored?.split === true,
    rules: normalizeSensorRules(stored?.rules, sensing).map((r) => ({ ...r, action: settle(r.action) })),
  };
}

/**
 * The pre-split shape: one flat `sensorRules` array, every entry a POOR-data row (no-data states
 * did not exist yet).
 *
 * Consolidates when every row agrees, because that is what the teacher was actually saying when
 * they set the same destination on each — and splits when they differ, so nobody's per-sensor
 * routing is silently flattened into whichever row happened to be first.
 */
/**
 * Reads the RAW stored flow, never the defaulted base.
 *
 * `{ ...DEFAULT_FLOW_CONFIG, ...flow }` gives both rungs a value before this runs, so asking the
 * base whether `poorSensorData` is missing always answers no and the legacy migration below would
 * never fire. The question is what was STORED, which only the original object can answer.
 */
function normalizeSensorRungs(
  stored: (Partial<TaskFlowConfig> & { sensorRules?: SensorRule[] }) | undefined,
  blocks: Block[],
  settle: (a: FlowAction) => FlowAction,
): Pick<TaskFlowConfig, 'noSensorData' | 'poorSensorData'> {
  const noData = noSensorDataBlocks(blocks);
  const poorData = poorSensorDataBlocks(blocks);
  // A config saved before the split carried one array, and every entry in it was a poor-data row.
  const legacy = stored?.sensorRules;
  return {
    noSensorData: normalizeSensorRung(stored?.noSensorData, noData, settle),
    poorSensorData: normalizeSensorRung(
      stored?.poorSensorData ?? (legacy ? migrateLegacySensorRules(legacy) : undefined),
      poorData,
      settle,
    ),
  };
}

/**
 * The arrival policy as stored, read defensively and with dead rows dropped.
 *
 * Keeps a per-block row only for a block that is still here, the way the sensor rungs do: a row
 * naming a deleted block is a decision about nothing, and keeping it means `split` silently
 * carries weight the panel cannot show. It does NOT backfill a row per block -- an absent row
 * follows the consolidated answer, which is what `arrivalKeepFor` says.
 *
 * Returns `undefined` for a task that has never been given one, so the field stays absent in the
 * payload rather than every task growing a default nobody chose.
 */
function normalizeArrival(raw: unknown, blocks: Block[]): ArrivalPolicy | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const stored = raw as Partial<ArrivalPolicy>;
  const here = new Set(blocks.map((b) => b.id));
  const keep = readKeep(stored.keep);
  return {
    keep,
    split: stored.split === true,
    rules: (Array.isArray(stored.rules) ? stored.rules : [])
      .filter((r): r is ArrivalRule => !!r && typeof r === 'object' && typeof r.blockId === 'string' && here.has(r.blockId))
      .map((r) => ({ blockId: r.blockId, ...readKeep(r) })),
  };
}

/**
 * Exit content as stored, read defensively, with anything that is not an allowed block dropped.
 *
 * THE PALETTE IS ENFORCED ON THE WAY IN as well as in the type: a paste is filtered at the
 * destination, but stored data can also arrive from a copy made before a type was removed, or
 * from a hand-edit. A block an exit may not hold could never render there anyway.
 *
 * The CAP is deliberately NOT enforced here. It belongs to the authoring surface; an exit that
 * somehow holds six renders six and lets the teacher delete down, because truncating authored
 * work to satisfy a limit is the worse failure.
 *
 * ORPHANS ARE NOT SWEPT HERE either. Content whose slot has gone, or been switched back to
 * `stay`, is pruned on LEAVING the Flow panel as its own undo step — the rule the orphan alias
 * sweep settled on. Dropping it at load would make undoing the change that orphaned it restore
 * a slot with nothing in it.
 *
 * Returns `undefined` when nothing survives, so the key disappears rather than leaving an empty
 * husk behind.
 */
function normalizeExits(raw: unknown): Record<string, ExitContent> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const allowed = new Set<string>(EXIT_BLOCK_TYPES);
  const out: Record<string, ExitContent> = {};
  for (const [slotId, content] of Object.entries(raw as Record<string, unknown>)) {
    const blocks = (content as ExitContent | undefined)?.blocks;
    if (!Array.isArray(blocks)) continue;
    const kept = blocks.filter(
      (b): b is ExitBlock => !!b && typeof b === 'object' && typeof b.id === 'string' && allowed.has(b.type),
    );
    if (kept.length > 0) out[slotId] = { blocks: kept };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Both halves default to FALSE, which is the clear-and-reset the model defaults to. */
function readKeep(raw: unknown): ArrivalKeep {
  const k = (raw ?? {}) as Partial<ArrivalKeep>;
  return { answers: k.answers === true, effects: k.effects === true };
}

function migrateLegacySensorRules(rules: SensorRule[]): SensorRung {
  if (rules.length === 0) return { action: { type: 'stay' }, split: false, rules: [] };
  const first = rules[0]!.action;
  const agree = rules.every((r) => JSON.stringify(r.action) === JSON.stringify(first));
  return { action: agree ? first : { type: 'stay' }, split: !agree, rules };
}

/**
 * Backfills any rule missing `title`/`matchMode`/`conditions` with sane defaults on load — a
 * rule saved before those fields existed (or any other malformed stored data) would otherwise
 * fail the backend's required-field validation on the *next* save, rejecting the whole request
 * even though nothing the teacher just did was wrong. Self-healing: the very next save rewrites
 * the repaired shape, same principle as block-naming.ts's backfillBlockNames.
 *
 * Also runs every rule through refreshConditionLabels against the just-loaded `blocks` — not
 * just for its usual label-refresh job, but because that's also where conditionAcknowledged gets
 * backfilled for any condition that doesn't have one yet (it only ever fills a MISSING entry,
 * never overwrites one that's already there — see refreshConditionLabels' own doc comment). A
 * rule authored before conditionAcknowledged/isRulePaused existed has no signature on record for
 * any of its conditions, so a change could never be detected for it — not because nothing had
 * changed, but because there was nothing on record to compare against. Backfilling on load means
 * every rule gets a real signature the first time it's loaded under this feature, exactly like
 * mathErrorReachable's own self-heal-on-mount for numerical blocks.
 */
export function normalizeFlowConfig(
  flow: TaskFlowConfig | undefined,
  blocks: Block[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale?: string,
  /**
   * The experience's LIVE tasks, for resolving goto targets. Optional: a caller without them
   * simply skips the target pass rather than guessing every route is dead.
   */
  tasks?: { id: string; title?: string }[],
): TaskFlowConfig {
  /**
   * `eventRules` / `defaultAction` were stored as `incorrectRules` / `incorrectDefaultAction`.
   *
   * The names were wrong, not just ugly: a Special Event matches on whatever conditions the
   * teacher wrote — a neutral target, an attempt count, a sensor state — and Default catches
   * "nothing above matched". Neither is about being incorrect, and the old names leaked onto the
   * board as an error-red ✕ on the Default wire.
   *
   * Read both, write the new one, exactly like `sensorRules` → `poorSensorData` above: the next
   * save rewrites the document and nothing has to be migrated ahead of time.
   */
  const legacyNames = flow as (TaskFlowConfig & { incorrectRules?: FlowRule[]; incorrectDefaultAction?: FlowAction }) | undefined;
  /*
   * READ BEFORE `base` SPREADS IT, because the spread would carry whatever was stored through
   * untouched -- `onArrival: 'yes'` and all. The key is then re-added only when there is a real
   * policy, so a task that has never been given one keeps the field absent rather than growing
   * a default nobody chose.
   */
  const arrival = normalizeArrival(flow?.onArrival, blocks);
  const exits = normalizeExits(flow?.exits);
  const base: TaskFlowConfig = {
    ...DEFAULT_FLOW_CONFIG,
    ...flow,
    onArrival: undefined,
    exits: undefined,
    eventRules: flow?.eventRules ?? legacyNames?.incorrectRules ?? DEFAULT_FLOW_CONFIG.eventRules,
    defaultAction: flow?.defaultAction ?? legacyNames?.incorrectDefaultAction ?? DEFAULT_FLOW_CONFIG.defaultAction,
  };
  // Only the CORRECT through-line may End; and an unresolved (null) goto is no longer authorable.
  // Coerce any legacy state on load so a stale End/null-goto can't render as an invisible/incomplete
  // action: a non-correct event falls back to its default (stay); a null goto anywhere becomes a
  // plain fall-through (correct → next, non-correct → stay).
  const noEndOrLoose = (a: FlowAction): FlowAction => (a.type === 'end' || (a.type === 'goto' && a.targetTaskId === null) ? { type: 'stay' } : a);
  const correctClean = (a: FlowAction): FlowAction => (a.type === 'goto' && a.targetTaskId === null ? { type: 'next' } : a);

  /**
   * A route to a task that still exists gets its title snapshot refreshed. A route to one that is
   * GONE is left exactly as it is.
   *
   * Leaving it is the point. The dangling id is the only evidence that this card used to route
   * somewhere, and `getCardProblem` reads it to pause the card and name the destination. Rewriting
   * it to `stay` on load -- the first thing tried here -- destroys that evidence and makes a dead
   * route indistinguishable from one that was authored as "stay", which is precisely the silent
   * reroute the pause exists to prevent.
   *
   * What made rewriting look necessary was the save trap: the backend used to REJECT any save
   * carrying a goto it could not resolve, so deleting one task made every task routing to it
   * unsaveable. That is fixed where it belongs, in the validator, which now tolerates a dead
   * target the same way it already tolerates a null one. The client is what surfaces it.
   */
  const liveTitle = new Map((tasks ?? []).map((tk) => [tk.id, tk.title ?? '']));
  const refreshTarget = (a: FlowAction): FlowAction => {
    if (!tasks || a.type !== 'goto' || a.targetTaskId === null || !liveTitle.has(a.targetTaskId)) return a;
    const title = liveTitle.get(a.targetTaskId) || undefined;
    return title === a.targetTitle ? a : { ...a, targetTitle: title };
  };

  const settleAction = (a: FlowAction): FlowAction => refreshTarget(noEndOrLoose(a));

  /**
   * The same snapshot refresh, for the task a `visits` condition names.
   *
   * Same rule as `refreshTarget` and for the same reason: a live target gets its label brought up
   * to date, a DELETED one keeps the stale label, because that label is the only thing left that
   * can name what the condition used to be about.
   */
  const refreshVisits = (conditions: FlowCondition[]): FlowCondition[] => conditions.map((c) => {
    if (!tasks || c.kind !== 'visits' || c.taskId === null || !liveTitle.has(c.taskId)) return c;
    const title = liveTitle.get(c.taskId) || undefined;
    return title === c.taskTitle ? c : { ...c, taskTitle: title };
  });
  return {
    ...base,
    ...(arrival ? { onArrival: arrival } : {}),
    ...(exits ? { exits } : {}),
    noAnswerAction: settleAction(base.noAnswerAction),
    defaultAction: settleAction(base.defaultAction),
    allCorrectAction: refreshTarget(correctClean(base.allCorrectAction)),
    eventRules: (base.eventRules ?? []).map((r, i) => refreshConditionLabels(
      migrateConditionIds({
        ...r,
        title: typeof r.title === 'string' && r.title ? r.title : t('task.flow.special.defaultTitle', { index: i + 1 }),
        matchMode: r.matchMode === 'any' || r.matchMode === 'all' ? r.matchMode : 'all',
        conditions: refreshVisits(migrateConditionShape(r.conditions)),
        action: settleAction(r.action),
      }, blocks),
      blocks, t, locale,
    )),
    ...normalizeSensorRungs(flow, blocks, settleAction),
    /*
     * Filtered before mapping, because a stored entry is not guaranteed to BE a change.
     * A real task was found holding `changes: [[]]` -- one entry, an empty array -- which
     * `normalizeChange` happily turned into a valid blank change, so the panel grew a card the
     * teacher never added, titled "Choose what this changes" and impossible to explain. An array
     * is an object, so the reject has to name arrays specifically.
     */
    changes: (base.changes ?? [])
      .filter((c): c is FlowChange => !!c && typeof c === 'object' && !Array.isArray(c))
      .map((c, i) => normalizeChange(c, i, refreshVisits)),
  };
}
